import React, { useEffect, useState, useRef } from 'react';
import apiClient from '../helpers/ApiClient';
import { toast } from 'react-hot-toast';
import { 
  IconCalendar, 
  IconWallet, 
  IconArrowUpRight, 
  IconArrowDownRight, 
  IconCash, 
  IconReceipt, 
  IconReportMoney, 
  IconX,
  IconLock,
  IconLockOpen,
  IconHistory,
  IconCheck,
  IconAlertCircle,
  IconClock,
  IconUser,
  IconNotes,
  IconDeviceFloppy,
  IconFileText,
  IconRefresh,
  IconCoins,
  IconMinus,
  IconPlus,
  IconChevronDown,
  IconChevronUp,
  IconExchange
} from '@tabler/icons-react';

import { useTranslation } from 'react-i18next';
import { useStoreSettings, getCurrencies } from '../controllers/settings.controller';
import { getUserDetailsInLocalStorage } from '../helpers/UserDetails';
import { PAYMENT_ICONS } from '../config/payment_icons';

const SafeIconPrinter = ({ size = 20, stroke = 2, className = "", ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
    <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
    <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z" />
  </svg>
);

export default function DayBookReportPage() {
  const { t } = useTranslation();
  const currentUser = getUserDetailsInLocalStorage() || {};
  const { data: storeSettings } = useStoreSettings();
  
  const storeName = storeSettings?.store_name || storeSettings?.storeName || storeSettings?.name || "Restaurant & Hotel";
  const storeAddress = storeSettings?.address || "";
  const storePhone = storeSettings?.phone || "";
  const storePan = storeSettings?.pan_no || storeSettings?.panNo || "";
  const currency = storeSettings?.currency || '$';



  const filters = [
    { key: "today", value: t("reports.filters.today") || "Today" },
    { key: "yesterday", value: t("reports.filters.yesterday") || "Yesterday" },
    { key: "last_7days", value: t("reports.filters.last_7days") || "Last 7 Days" },
    { key: "this_month", value: t("reports.filters.this_month") || "This Month" },
    { key: "last_month", value: t("reports.filters.last_month") || "Last Month" },
    { key: "custom", value: t("reports.filters.custom") || "Custom Date" },
  ];

  // Day book filter states
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [filterType, setFilterType] = useState('today');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isSavingOB, setIsSavingOB] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  // Shifts state
  const [currentShift, setCurrentShift] = useState(null);
  const [loadingShift, setLoadingShift] = useState(false);
  const [shiftsHistory, setShiftsHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Modals state
  const [isStartShiftModalOpen, setIsStartShiftModalOpen] = useState(false);
  const [isEndShiftModalOpen, setIsEndShiftModalOpen] = useState(false);
  const [isVoucherModalOpen, setIsVoucherModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedVoucherShift, setSelectedVoucherShift] = useState(null);
  const [printMode, setPrintMode] = useState('daybook'); // 'daybook' or 'voucher'

  // Start shift form
  const [startFloat, setStartFloat] = useState('0');
  const [startNote, setStartNote] = useState('');
  const [startingShiftLoading, setStartingShiftLoading] = useState(false);

  // End shift form
  const [actualCash, setActualCash] = useState('');
  const [actualQr, setActualQr] = useState('');
  const [actualExpenses, setActualExpenses] = useState('');
  const [closingFloatRetained, setClosingFloatRetained] = useState('0');
  const [cashDeposited, setCashDeposited] = useState('');
  const [closingNote, setClosingNote] = useState('');
  const [endingShiftLoading, setEndingShiftLoading] = useState(false);

  // Multi-Currency & Denominations for End Shift & Start Shift / OB
  const [currenciesList, setCurrenciesList] = useState([]);
  const [selectedCurrencyCode, setSelectedCurrencyCode] = useState("");
  const [denominationCounts, setDenominationCounts] = useState({}); // { [currCode]: { [denom]: count } }
  const [startDenominationCounts, setStartDenominationCounts] = useState({});
  const [obDenominationCounts, setObDenominationCounts] = useState({});
  const [isOBDenomModalOpen, setIsOBDenomModalOpen] = useState(false);
  const [exchangeRatesState, setExchangeRatesState] = useState({}); // { [currCode]: rate }
  const [showDenominationSection, setShowDenominationSection] = useState(true);

  const buildDenominationPayload = (countsMap, list, ratesMap) => {
    if (!countsMap || !list || list.length === 0) return null;
    const result = [];
    (list || []).forEach((c) => {
      const counts = countsMap[c.code] || {};
      const rate = parseFloat(ratesMap?.[c.code] ?? c.exchange_rate ?? 1.0);
      let currTotal = 0;
      const breakdown = [];
      (c.denominations || []).forEach((d) => {
        const count = parseInt(counts[d], 10) || 0;
        if (count > 0) {
          const itemTotal = Number(d) * count;
          currTotal += itemTotal;
          breakdown.push({
            denomination: Number(d),
            count: count,
            total: itemTotal
          });
        }
      });
      if (breakdown.length > 0) {
        result.push({
          currency_code: c.code,
          currency_symbol: c.symbol,
          is_base: Boolean(c.is_base),
          exchange_rate: rate,
          currency_total: currTotal,
          base_total: currTotal * rate,
          breakdown: breakdown
        });
      }
    });
    return result.length > 0 ? result : null;
  };

  // Fetch current shift
  const fetchCurrentShift = async () => {
    try {
      setLoadingShift(true);
      const res = await apiClient.get('/reports/day-book/current-shift');
      if (res.data?.success) {
        let active = null;
        if (res.data.hasActiveShift) {
          active = res.data.shift?.currentShift || res.data.shift || null;
        } else if (res.data.shift && res.data.shift.id && res.data.shift.status === 'open') {
          active = res.data.shift;
        } else if (res.data.shift?.currentShift && res.data.shift.currentShift.id) {
          active = res.data.shift.currentShift;
        }
        if (active && !active.id) {
          active = null;
        }
        setCurrentShift(active);
      } else {
        setCurrentShift(null);
      }
    } catch (error) {
      console.error("fetchCurrentShift error:", error);
      setCurrentShift(null);
    } finally {
      setLoadingShift(false);
    }
  };

  // Fetch shifts history
  const fetchShiftsHistory = async () => {
    try {
      setLoadingHistory(true);
      const res = await apiClient.get('/reports/day-book/shifts?limit=30');
      if (res.data?.success) {
        setShiftsHistory(res.data.shifts || []);
      }
    } catch (error) {
      console.error("fetchShiftsHistory error:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch Day Book Report
  const fetchReport = async () => {
    try {
      setLoading(true);
      let query = `?type=${filterType}`;
      if(filterType === 'custom') {
        query += `&from=${fromDate}&to=${toDate}`;
      } else {
        query += `&from=${date}&to=${date}`;
      }
      const res = await apiClient.get(`/reports/day-book${query}`);
      setReport(res.data);
      if(res.data?.opening_balance !== undefined) {
         setOpeningBalance(res.data.opening_balance);
      }
    } catch (error) {
      toast.error(t("something_went_wrong_try_later") || "Something went wrong fetching report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    fetchCurrentShift();
  }, [date, filterType, fromDate, toDate]);

  const saveOpeningBalance = async () => {
    try {
      setIsSavingOB(true);
      const targetDate = filterType === 'custom' && fromDate ? fromDate : date;
      const obDenomsPayload = buildDenominationPayload(obDenominationCounts, currenciesList, exchangeRatesState);
      await apiClient.post('/reports/day-book/opening-balance', { 
        date: targetDate, 
        amount: openingBalance,
        denominations: obDenomsPayload
      });
      toast.success("Opening balance saved successfully");
    } catch (error) {
      toast.error("Failed to save opening balance");
    } finally {
      setIsSavingOB(false);
    }
  };

  // Open start shift modal
  const handleOpenStartShift = async () => {
    setStartFloat('0');
    setStartNote('');
    setStartDenominationCounts({});
    try {
      const currs = await getCurrencies();
      const list = currs || [];
      setCurrenciesList(list);
      const ratesObj = {};
      list.forEach((c) => {
        ratesObj[c.code] = parseFloat(c.exchange_rate) || 1.0;
      });
      setExchangeRatesState(ratesObj);
    } catch (e) {}
    setIsStartShiftModalOpen(true);
  };

  const handleOpenOBDenominations = async () => {
    try {
      const currs = await getCurrencies();
      const list = currs || [];
      setCurrenciesList(list);
      const ratesObj = {};
      list.forEach((c) => {
        ratesObj[c.code] = parseFloat(c.exchange_rate) || 1.0;
      });
      setExchangeRatesState(ratesObj);
    } catch (e) {}
    setIsOBDenomModalOpen(true);
  };

  // Submit start shift
  const handleStartShiftSubmit = async (e) => {
    e.preventDefault();
    try {
      setStartingShiftLoading(true);
      const startDenomsPayload = buildDenominationPayload(startDenominationCounts, currenciesList, exchangeRatesState);
      const res = await apiClient.post('/reports/day-book/start-shift', {
        startingFloat: parseFloat(startFloat) || 0,
        note: startNote,
        startDenominations: startDenomsPayload
      });
      if (res.data?.success) {
        toast.success(res.data.message || "Shift started successfully!");
        const newShift = res.data.shift?.currentShift || res.data.shift || null;
        setCurrentShift(newShift && newShift.id ? newShift : null);
        setIsStartShiftModalOpen(false);
        fetchCurrentShift();
      } else {
        toast.error(res.data?.message || "Failed to start shift");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to start shift");
    } finally {
      setStartingShiftLoading(false);
    }
  };

  // Open end shift modal and pre-fill system numbers
  const handleOpenEndShift = async () => {
    await fetchCurrentShift();
    const activeShift = (currentShift && currentShift.id) ? currentShift : (currentShift?.currentShift?.id ? currentShift.currentShift : null);
    if (!activeShift?.id) {
      toast.error("No active shift found to close");
      return;
    }
    const stats = activeShift?.current_stats || {};
    const floatVal = parseFloat(activeShift?.opening_balance ?? activeShift?.starting_float ?? 0);
    const expected = parseFloat(stats.expected_cash ?? floatVal ?? 0);
    const recordedExpenses = parseFloat(stats.expenses ?? 0);
    const recordedQr = parseFloat(stats.qr_sales ?? 0);

    setActualCash(expected.toFixed(2));
    setActualQr(recordedQr.toFixed(2));
    setActualExpenses(recordedExpenses.toFixed(2));
    setClosingFloatRetained('0');
    setCashDeposited(expected.toFixed(2));
    setClosingNote('');

    // Fetch tenant's configured currencies and denominations
    try {
      const currs = await getCurrencies();
      const list = currs || [];
      setCurrenciesList(list);
      const baseCurr = list.find((c) => c.is_base) || list[0] || { code: "NPR" };
      setSelectedCurrencyCode(baseCurr.code);

      const ratesObj = {};
      list.forEach((c) => {
        ratesObj[c.code] = parseFloat(c.exchange_rate) || 1.0;
      });
      setExchangeRatesState(ratesObj);
      setDenominationCounts({});
    } catch (e) {
      console.warn("Could not fetch currencies:", e);
    }

    setIsEndShiftModalOpen(true);
  };

  // Live calculation of difference and cash deposited
  const shiftStats = currentShift?.current_stats || {};
  const currentFloat = parseFloat(currentShift?.opening_balance ?? currentShift?.starting_float ?? 0);
  const currentExpectedCash = parseFloat(shiftStats.expected_cash ?? currentFloat ?? 0);
  const parsedActualCash = parseFloat(actualCash) || 0;
  const parsedRetained = parseFloat(closingFloatRetained) || 0;
  const cashDifference = parsedActualCash - currentExpectedCash;

  // Auto-sync deposit when actualCash or closingFloatRetained changes
  const handleActualCashChange = (val) => {
    setActualCash(val);
    const cashVal = parseFloat(val) || 0;
    const retainedVal = parseFloat(closingFloatRetained) || 0;
    setCashDeposited((cashVal - retainedVal).toFixed(2));
  };

  const handleRetainedChange = (val) => {
    setClosingFloatRetained(val);
    const retainedVal = parseFloat(val) || 0;
    const cashVal = parseFloat(actualCash) || 0;
    setCashDeposited((cashVal - retainedVal).toFixed(2));
  };

  // Compute denomination totals across all currencies
  const computeDenominationTotals = () => {
    let grandBaseTotal = 0;
    const perCurrencyTotals = {};

    (currenciesList || []).forEach((c) => {
      const counts = denominationCounts[c.code] || {};
      const rate = parseFloat(exchangeRatesState[c.code] ?? c.exchange_rate ?? 1.0);
      let currTotal = 0;
      (c.denominations || []).forEach((d) => {
        const count = parseFloat(counts[d]) || 0;
        currTotal += Number(d) * count;
      });
      const baseVal = currTotal * rate;
      perCurrencyTotals[c.code] = {
        currencyTotal: currTotal,
        baseTotal: baseVal,
        rate
      };
      grandBaseTotal += baseVal;
    });

    return { grandBaseTotal, perCurrencyTotals };
  };

  const { grandBaseTotal, perCurrencyTotals } = computeDenominationTotals();

  const handleDenomCountChange = (currCode, denomVal, countVal) => {
    const parsedVal = countVal === '' ? '' : Math.max(0, parseInt(countVal, 10) || 0);
    const newCounts = {
      ...denominationCounts,
      [currCode]: {
        ...(denominationCounts[currCode] || {}),
        [denomVal]: parsedVal
      }
    };
    setDenominationCounts(newCounts);

    // Compute updated grand total and sync to actualCash
    let newGrandTotal = 0;
    (currenciesList || []).forEach((c) => {
      const counts = newCounts[c.code] || {};
      const rate = parseFloat(exchangeRatesState[c.code] ?? c.exchange_rate ?? 1.0);
      let currTotal = 0;
      (c.denominations || []).forEach((d) => {
        const cnt = parseFloat(counts[d]) || 0;
        currTotal += Number(d) * cnt;
      });
      newGrandTotal += currTotal * rate;
    });

    if (newGrandTotal > 0) {
      handleActualCashChange(newGrandTotal.toFixed(2));
    }
  };

  const handleApplyDenominationsToActualCash = () => {
    handleActualCashChange(grandBaseTotal.toFixed(2));
    toast.success(`Drawer cash updated to ${currency}${grandBaseTotal.toFixed(2)} from counted denominations`);
  };

  const handleShiftExchangeRateChange = (currCode, newRate) => {
    const parsedRate = parseFloat(newRate) || 0;
    const newRates = { ...exchangeRatesState, [currCode]: parsedRate };
    setExchangeRatesState(newRates);

    let newGrandTotal = 0;
    (currenciesList || []).forEach((c) => {
      const counts = denominationCounts[c.code] || {};
      const rate = parseFloat(newRates[c.code] ?? c.exchange_rate ?? 1.0);
      let currTotal = 0;
      (c.denominations || []).forEach((d) => {
        const cnt = parseFloat(counts[d]) || 0;
        currTotal += Number(d) * cnt;
      });
      newGrandTotal += currTotal * rate;
    });

    if (newGrandTotal > 0) {
      handleActualCashChange(newGrandTotal.toFixed(2));
    }
  };

  // Submit end shift
  const handleEndShiftSubmit = async (e) => {
    e.preventDefault();
    const activeShiftId = currentShift?.id || currentShift?.currentShift?.id;
    if (!activeShiftId) {
      toast.error("No active shift found to close");
      return;
    }

    // Build structured denomination breakdown payload
    const denominationPayload = (currenciesList || []).map((c) => {
      const counts = denominationCounts[c.code] || {};
      const rate = parseFloat(exchangeRatesState[c.code] ?? c.exchange_rate ?? 1.0);
      const breakdown = (c.denominations || [])
        .map((d) => ({
          denomination: Number(d),
          count: Number(counts[d] || 0),
          total: Number(d) * Number(counts[d] || 0)
        }))
        .filter((item) => item.count > 0);

      const currTotal = breakdown.reduce((sum, item) => sum + item.total, 0);
      const baseTotal = currTotal * rate;

      return {
        currency_code: c.code,
        currency_symbol: c.symbol,
        exchange_rate: rate,
        is_base: Boolean(c.is_base),
        breakdown,
        currency_total: currTotal,
        base_total: baseTotal
      };
    }).filter((c) => c.breakdown.length > 0 || c.currency_total > 0);

    try {
      setEndingShiftLoading(true);
      const res = await apiClient.post('/reports/day-book/end-shift', {
        shiftId: activeShiftId,
        actualCash: parsedActualCash,
        actualQr: parseFloat(actualQr) || 0,
        actualExpenses: parseFloat(actualExpenses) || 0,
        closingBalance: parsedRetained,
        cashDeposited: parseFloat(cashDeposited) || (parsedActualCash - parsedRetained),
        note: closingNote,
        denominations: denominationPayload.length > 0 ? denominationPayload : null
      });

      if (res.data?.success) {
        toast.success(res.data.message || "Shift closed successfully!");
        setIsEndShiftModalOpen(false);
        setCurrentShift(null);
        setSelectedVoucherShift(res.data.shift);
        setIsVoucherModalOpen(true);
        fetchReport();
        fetchCurrentShift();
      } else {
        toast.error(res.data?.message || "Failed to close shift");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to close shift");
    } finally {
      setEndingShiftLoading(false);
    }
  };

  // Open Voucher for a specific shift
  const handleViewVoucher = (shift) => {
    setSelectedVoucherShift(shift);
    setIsVoucherModalOpen(true);
  };

  // Print handlers
  const handlePrintDayBook = () => {
    setPrintMode('daybook');
    setTimeout(() => {
      window.print();
    }, 150);
  };

  const generateVoucherHTML = (shift, meta) => {
    const { storeName, storeAddress, storePhone, storePan, currency, currentUser, formatDateTime } = meta;
    const shiftNumber = shift.shift_number || '';
    const openedBy = shift.opened_by_name || 'Cashier';
    const openedAt = formatDateTime(shift.opened_at);
    const closedBy = shift.closed_by_name || currentUser?.name || 'Cashier';
    const closedAt = formatDateTime(shift.closed_at);
    const openingFloat = parseFloat(shift.opening_balance ?? shift.starting_float ?? 0).toFixed(2);
    const expenses = parseFloat(shift.actual_expenses || shift.system_expenses || 0).toFixed(2);
    const expectedCash = parseFloat(shift.system_expected_cash || 0).toFixed(2);
    const actualCash = parseFloat(shift.actual_cash_in_hand || 0).toFixed(2);
    const actualQr = parseFloat(shift.actual_qr_in_hand || 0).toFixed(2);
    const diffVal = parseFloat(shift.cash_difference ?? shift.difference ?? 0);
    const diffSign = diffVal >= 0 ? '+' : '';
    const diffLabel = diffVal === 0 ? ' (Balanced)' : diffVal > 0 ? ' (Over)' : ' (Short)';
    const diffColor = diffVal === 0 ? '#059669' : diffVal > 0 ? '#2563eb' : '#dc2626';
    const closingFloat = parseFloat(shift.closing_balance || 0).toFixed(2);
    const cashDeposited = parseFloat(shift.cash_deposited || (shift.actual_cash_in_hand - shift.closing_balance) || 0).toFixed(2);

    let paymentRows = '';
    if (shift.payment_breakdown && shift.payment_breakdown.length > 0) {
      paymentRows = shift.payment_breakdown.map(pb => `
        <tr class="border-b">
          <td style="padding: 4px 0;">Sales (${pb.payment_method})</td>
          <td style="padding: 4px 0; text-align: right; font-weight: bold;">${currency}${parseFloat(pb.total_amount || 0).toFixed(2)}</td>
        </tr>
      `).join('');
    } else {
      paymentRows = `
        <tr class="border-b">
          <td style="padding: 4px 0;">Total Cash Sales</td>
          <td style="padding: 4px 0; text-align: right; font-weight: bold;">${currency}${parseFloat(shift.system_cash_sales || 0).toFixed(2)}</td>
        </tr>
        <tr class="border-b">
          <td style="padding: 4px 0;">Total QR / UPI Sales</td>
          <td style="padding: 4px 0; text-align: right; font-weight: bold;">${currency}${parseFloat(shift.system_qr_sales || 0).toFixed(2)}</td>
        </tr>
        ${parseFloat(shift.system_card_sales || 0) > 0 ? `
          <tr class="border-b">
            <td style="padding: 4px 0;">Total Card Sales</td>
            <td style="padding: 4px 0; text-align: right; font-weight: bold;">${currency}${parseFloat(shift.system_card_sales).toFixed(2)}</td>
          </tr>
        ` : ''}
      `;
    }

    const dueCollectedRow = parseFloat(shift.system_due_collected || 0) > 0 ? `
      <tr class="border-b">
        <td style="padding: 4px 0;">Credit / Due Collected</td>
        <td style="padding: 4px 0; text-align: right; font-weight: bold;">${currency}${parseFloat(shift.system_due_collected).toFixed(2)}</td>
      </tr>
    ` : '';

    const duePaidRow = parseFloat(shift.system_due_paid || 0) > 0 ? `
      <tr class="border-b">
        <td style="padding: 4px 0; color: #be123c;">Supplier Due Paid</td>
        <td style="padding: 4px 0; text-align: right; font-weight: bold; color: #be123c;">(${currency}${parseFloat(shift.system_due_paid).toFixed(2)})</td>
      </tr>
    ` : '';

    const transferOutRow = parseFloat(shift.shift_cash_transfers_out || 0) > 0 ? `
      <tr class="border-b">
        <td style="padding: 4px 0; color: #be123c;">Cash Transferred Out (Contra)</td>
        <td style="padding: 4px 0; text-align: right; font-weight: bold; color: #be123c;">(${currency}${parseFloat(shift.shift_cash_transfers_out).toFixed(2)})</td>
      </tr>
    ` : '';

    const transferInRow = parseFloat(shift.shift_cash_transfers_in || 0) > 0 ? `
      <tr class="border-b">
        <td style="padding: 4px 0; color: #047857;">Cash Transferred In (Contra)</td>
        <td style="padding: 4px 0; text-align: right; font-weight: bold; color: #047857;">+${currency}${parseFloat(shift.shift_cash_transfers_in).toFixed(2)}</td>
      </tr>
    ` : '';

    const journalOutRow = parseFloat(shift.shift_cash_journal_out || 0) > 0 ? `
      <tr class="border-b">
        <td style="padding: 4px 0; color: #be123c;">Cash Journal Outflow</td>
        <td style="padding: 4px 0; text-align: right; font-weight: bold; color: #be123c;">(${currency}${parseFloat(shift.shift_cash_journal_out).toFixed(2)})</td>
      </tr>
    ` : '';

    const journalInRow = parseFloat(shift.shift_cash_journal_in || 0) > 0 ? `
      <tr class="border-b">
        <td style="padding: 4px 0; color: #047857;">Cash Journal Inflow</td>
        <td style="padding: 4px 0; text-align: right; font-weight: bold; color: #047857;">+${currency}${parseFloat(shift.shift_cash_journal_in).toFixed(2)}</td>
      </tr>
    ` : '';

    let notesHtml = '';
    if (shift.notes || shift.opening_note || shift.closing_note) {
      notesHtml = `
        <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px; margin: 12px 0; font-size: 11px;">
          ${shift.notes ? `<p style="margin: 2px 0;"><b>Notes:</b> ${shift.notes}</p>` : ''}
          ${shift.opening_note ? `<p style="margin: 2px 0;"><b>Opening Note:</b> ${shift.opening_note}</p>` : ''}
          ${shift.closing_note ? `<p style="margin: 2px 0;"><b>Closing Note:</b> ${shift.closing_note}</p>` : ''}
        </div>
      `;
    }

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Shift Closing Voucher - Shift #${shiftNumber}</title>
        <style>
          @page { size: auto; margin: 8mm; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
            margin: 0; padding: 15px; color: #111; background: #fff; font-size: 12px; line-height: 1.4;
          }
          .container { max-width: 650px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 12px; }
          .store-name { font-size: 20px; font-weight: 900; text-transform: uppercase; margin: 0 0 4px 0; }
          .store-info { font-size: 11px; color: #444; margin: 2px 0; }
          .report-title { font-size: 13px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; margin-top: 6px; text-decoration: underline; }
          .grid-meta { display: flex; justify-content: space-between; border-bottom: 1px solid #ddd; padding-bottom: 8px; margin-bottom: 12px; font-size: 11px; }
          .meta-col { flex: 1; }
          .meta-col.right { text-align: right; }
          .meta-col p { margin: 2px 0; }
          .section-title { font-size: 11px; font-weight: bold; text-transform: uppercase; color: #333; border-bottom: 1px solid #ccc; padding-bottom: 3px; margin: 10px 0 6px 0; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 8px; }
          td { padding: 4px 0; }
          tr.border-b td { border-bottom: 1px solid #eee; }
          .text-right { text-align: right; }
          .font-bold { font-weight: bold; }
          .highlight-row { background: #f3f4f6; border-top: 2px solid #000; }
          .highlight-row td { padding: 6px 4px; font-weight: bold; }
          .deposit-row { background: #ecfdf5; border-top: 2px solid #059669; }
          .deposit-row td { padding: 6px 4px; color: #065f46; font-weight: bold; }
          .signatures { margin-top: 35px; padding-top: 15px; border-top: 1px solid #ddd; display: flex; justify-content: space-between; text-align: center; page-break-inside: avoid; }
          .sig-block { flex: 1; display: flex; flex-direction: column; align-items: center; }
          .sig-line { width: 120px; border-bottom: 1px dashed #666; height: 30px; margin-bottom: 4px; }
          .sig-title { font-weight: bold; font-size: 10px; text-transform: uppercase; }
          .sig-sub { font-size: 9px; color: #666; }
          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="store-name">${storeName || 'Restaurant'}</div>
            ${storeAddress ? `<div class="store-info">${storeAddress}</div>` : ''}
            <div class="store-info">
              ${storePhone ? `<span>Tel: ${storePhone}</span> ` : ''}
              ${storePan ? `<span>PAN/VAT: ${storePan}</span>` : ''}
            </div>
            <div class="report-title">REGISTER SHIFT CLOSING REPORT (Z-REPORT)</div>
          </div>

          <div class="grid-meta">
            <div class="meta-col">
              <p><b>Shift No:</b> #${shiftNumber}</p>
              <p><b>Opened By:</b> ${openedBy}</p>
              <p><b>Opened At:</b> ${openedAt}</p>
            </div>
            <div class="meta-col right">
              <p><b>Status:</b> CLOSED</p>
              <p><b>Closed By:</b> ${closedBy}</p>
              <p><b>Closed At:</b> ${closedAt}</p>
            </div>
          </div>

          <div class="section-title">1. Shift Financial Summary</div>
          <table>
            <tbody>
              <tr class="border-b">
                <td>Opening Cash Float</td>
                <td class="text-right font-bold">${currency}${openingFloat}</td>
              </tr>
              ${paymentRows}
              ${dueCollectedRow}
              <tr class="border-b">
                <td style="color: #be123c;">Shift Expenses Paid</td>
                <td class="text-right font-bold" style="color: #be123c;">(${currency}${expenses})</td>
              </tr>
              ${duePaidRow}
              ${transferOutRow}
              ${transferInRow}
              ${journalOutRow}
              ${journalInRow}
              <tr class="highlight-row">
                <td>System Expected Cash in Drawer</td>
                <td class="text-right">${currency}${expectedCash}</td>
              </tr>
            </tbody>
          </table>

          <div class="section-title">2. Physical Drawer Audit & Reconciliation</div>
          <table>
            <tbody>
              <tr class="border-b">
                <td>Actual Physical Cash Counted</td>
                <td class="text-right font-bold" style="font-size: 13px;">${currency}${actualCash}</td>
              </tr>
              <tr class="border-b">
                <td>Actual QR / Online Verified</td>
                <td class="text-right font-bold">${currency}${actualQr}</td>
              </tr>
              <tr class="border-b font-bold">
                <td>Cash Difference / Variance</td>
                <td class="text-right" style="color: ${diffColor};">
                  ${diffSign}${currency}${Math.abs(diffVal).toFixed(2)}${diffLabel}
                </td>
              </tr>
              <tr class="border-b">
                <td>Closing Float Retained for Next Shift</td>
                <td class="text-right font-bold">${currency}${closingFloat}</td>
              </tr>
              <tr class="deposit-row">
                <td>Net Cash Handed Over / Safe Deposit</td>
                <td class="text-right font-bold" style="font-size: 13px;">${currency}${cashDeposited}</td>
              </tr>
            </tbody>
          </table>

          ${notesHtml}

          <div class="signatures">
            <div class="sig-block">
              <div class="sig-line"></div>
              <div class="sig-title">Prepared By</div>
              <div class="sig-sub">(${closedBy})</div>
            </div>
            <div class="sig-block">
              <div class="sig-line"></div>
              <div class="sig-title">Verified By</div>
              <div class="sig-sub">(Supervisor)</div>
            </div>
            <div class="sig-block">
              <div class="sig-line"></div>
              <div class="sig-title">Approved By</div>
              <div class="sig-sub">(Manager / Owner)</div>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handlePrintVoucher = () => {
    if (!selectedVoucherShift) return;
    const html = generateVoucherHTML(selectedVoucherShift, {
      storeName,
      storeAddress,
      storePhone,
      storePan,
      currency,
      currentUser,
      formatDateTime
    });

    // Strategy 1: Hidden iframe print (100% reliable across Electron and browsers, zero blank page issues)
    try {
      let iframe = document.getElementById("voucher-print-iframe");
      if (!iframe) {
        iframe = document.createElement("iframe");
        iframe.id = "voucher-print-iframe";
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "none";
        document.body.appendChild(iframe);
      }
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      }, 300);
      return;
    } catch (e) {
      console.warn("Iframe print failed, trying window.open", e);
    }

    // Strategy 2: Popup window
    try {
      const printWindow = window.open("", "_blank", "width=850,height=750");
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 300);
        return;
      }
    } catch (e) {
      console.warn("Popup print failed, falling back to window.print", e);
    }

    // Strategy 3: In-page print fallback
    setPrintMode('voucher');
    setTimeout(() => {
      window.print();
    }, 200);
  };

  const getSalesTotal = () => report?.sales_payments?.reduce((acc, curr) => acc + (parseFloat(curr.total_amount) || 0), 0) || 0;
  const getPurchasesTotal = () => report?.purchase_payments?.reduce((acc, curr) => acc + (parseFloat(curr.total_amount) || 0), 0) || 0;
  const getTransfersInTotal = () => parseFloat(report?.total_transfers_in) || 0;
  const getTransfersOutTotal = () => parseFloat(report?.total_transfers_out) || 0;
  const getJournalInTotal = () => parseFloat(report?.total_journal_in) || 0;
  const getJournalOutTotal = () => parseFloat(report?.total_journal_out) || 0;

  const totalCashIn = (parseFloat(openingBalance) || 0) + getSalesTotal() + getTransfersInTotal() + getJournalInTotal();
  const totalCashOut = (parseFloat(report?.total_expenses) || 0) + getPurchasesTotal() + getTransfersOutTotal() + getJournalOutTotal();
  const calcClosingBalance = totalCashIn - totalCashOut;

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      {/* Top Header & Shift Controller */}
      <div className="no-print space-y-4 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 text-gray-800 dark:text-white">
              <IconReportMoney size={28} className="text-emerald-600" />
              Day Book & Register Control
            </h1>
            <p className="text-sm text-gray-500">
              Shift reconciliation, cash drawer tracking, and daily cash flow report.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => {
                fetchShiftsHistory();
                setIsHistoryModalOpen(true);
              }}
              className="btn btn-sm btn-outline gap-1"
            >
              <IconHistory size={16} /> Closed Shifts History
            </button>
            <button 
              onClick={handlePrintDayBook} 
              className="btn btn-sm btn-neutral gap-1"
            >
              <SafeIconPrinter size={16} /> Print Day Book
            </button>
          </div>
        </div>

        {/* ACTIVE SHIFT BANNER */}
        <div className="rounded-2xl border p-4 shadow-sm transition-all duration-200 bg-white">
          {loadingShift ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
              <span className="loading loading-spinner loading-sm"></span> Loading register shift status...
            </div>
          ) : (currentShift && currentShift.id) ? (
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 animate-pulse">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span> ACTIVE SHIFT #{currentShift.shift_number}
                  </span>
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <IconClock size={14} /> Opened {formatDateTime(currentShift.opened_at)}
                  </span>
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <IconUser size={14} /> Cashier: <b>{currentShift.opened_by_name || 'Cashier'}</b>
                  </span>
                </div>

                {/* Shift live numbers */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 pt-2">
                  <div className="bg-gray-50 border rounded-xl p-2.5">
                    <div className="text-xs text-gray-500">Opening Float</div>
                    <div className="text-sm font-bold text-gray-800">{currency}{parseFloat(currentShift.opening_balance ?? currentShift.starting_float ?? 0).toFixed(2)}</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-2.5">
                    <div className="text-xs text-emerald-700">Cash Sales</div>
                    <div className="text-sm font-bold text-emerald-800">{currency}{parseFloat(currentShift.current_stats?.cash_sales || 0).toFixed(2)}</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-2.5">
                    <div className="text-xs text-blue-700">QR / UPI Sales</div>
                    <div className="text-sm font-bold text-blue-800">{currency}{parseFloat(currentShift.current_stats?.qr_sales || 0).toFixed(2)}</div>
                  </div>
                  <div className="bg-rose-50 border border-rose-100 rounded-xl p-2.5">
                    <div className="text-xs text-rose-700">Shift Expenses</div>
                    <div className="text-sm font-bold text-rose-800">{currency}{parseFloat(currentShift.current_stats?.expenses || 0).toFixed(2)}</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-2.5">
                    <div className="text-xs text-amber-700 font-medium">Expected Drawer Cash</div>
                    <div className="text-sm font-extrabold text-amber-900">{currency}{parseFloat(currentShift.current_stats?.expected_cash || 0).toFixed(2)}</div>
                  </div>
                </div>

                {currentShift.current_stats?.payment_breakdown?.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 pt-2 text-xs">
                    <span className="text-gray-400 font-medium">Shift Collections:</span>
                    {currentShift.current_stats.payment_breakdown.map((pb, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-gray-50 border text-gray-700 font-medium">
                        <span className="w-3.5 h-3.5 flex items-center justify-center text-emerald-600">
                          {PAYMENT_ICONS[pb.icon] || <IconCash size={12} />}
                        </span>
                        <span>{pb.payment_method}:</span>
                        <b className="text-gray-900">{currency}{parseFloat(pb.total_amount || 0).toFixed(2)}</b>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 self-start lg:self-center">
                <button
                  onClick={fetchCurrentShift}
                  className="btn btn-sm btn-ghost btn-square"
                  title="Refresh Live Shift Stats"
                >
                  <IconRefresh size={18} />
                </button>
                <button
                  onClick={handleOpenEndShift}
                  className="btn btn-sm bg-rose-600 hover:bg-rose-700 text-white border-none gap-1.5 shadow-sm px-4"
                >
                  <IconLock size={16} /> End Shift & Close Register
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold">
                  <IconLockOpen size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-800">Register is currently closed</h3>
                  <p className="text-xs text-gray-500">Start a new cashier shift with an opening cash float to begin tracking drawer activity.</p>
                </div>
              </div>
              <button
                onClick={handleOpenStartShift}
                className="btn btn-sm bg-emerald-600 hover:bg-emerald-700 text-white border-none gap-1.5 shadow-sm px-4"
              >
                <IconLockOpen size={16} /> Start Shift / Open Register
              </button>
            </div>
          )}
        </div>

        {/* DAY BOOK FILTERS & OPENING BALANCE */}
        <div className="bg-white p-4 rounded-2xl border shadow-sm flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="form-control">
              <label className="label cursor-pointer p-0 mb-1"><span className="label-text text-xs font-semibold text-gray-600">Time Filter</span></label>
              <select className="select select-sm select-bordered" value={filterType} onChange={e => setFilterType(e.target.value)}>
                {filters.map(f => <option key={f.key} value={f.key}>{f.value}</option>)}
              </select>
            </div>

            {filterType === 'custom' && (
              <>
                <div className="form-control">
                  <label className="label cursor-pointer p-0 mb-1"><span className="label-text text-xs font-semibold text-gray-600">From</span></label>
                  <input type="date" className="input input-sm input-bordered" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                </div>
                <div className="form-control">
                  <label className="label cursor-pointer p-0 mb-1"><span className="label-text text-xs font-semibold text-gray-600">To</span></label>
                  <input type="date" className="input input-sm input-bordered" value={toDate} onChange={e => setToDate(e.target.value)} />
                </div>
              </>
            )}

            <div className="form-control">
              <label className="label cursor-pointer p-0 mb-1"><span className="label-text text-xs font-semibold text-gray-600">General Opening Balance ({currency})</span></label>
              <div className="flex items-center gap-2">
                <input 
                  type="number" 
                  step="0.01" 
                  className="input input-sm input-bordered w-28" 
                  value={openingBalance} 
                  onChange={e => setOpeningBalance(e.target.value)} 
                />
                <button 
                  type="button"
                  onClick={handleOpenOBDenominations}
                  className="btn btn-sm btn-outline border-amber-400 text-amber-800 hover:bg-amber-50 gap-1 px-2"
                  title="Count Opening Balance Denominations"
                >
                  <IconCoins size={14} /> Notes
                </button>
                <button 
                  className="btn btn-sm btn-outline btn-success" 
                  onClick={saveOpeningBalance} 
                  disabled={isSavingOB}
                >
                  {isSavingOB ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          <div className="text-right">
            <span className="text-xs text-gray-500">Period: </span>
            <span className="text-xs font-bold text-gray-700">
              {filterType === 'custom' ? `${fromDate || '-'} to ${toDate || '-'}` : filters.find(f => f.key === filterType)?.value}
            </span>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center py-16 no-print">
          <span className="loading loading-spinner loading-lg text-primary"></span>
          <p className="text-sm text-gray-500 mt-2">Loading day book records...</p>
        </div>
      )}

      {/* DAY BOOK REPORT CONTENT (Screen & Print) */}
      {!loading && report && (
        <div className={`daybook-printable ${printMode === 'daybook' ? 'print-active' : ''}`}>
          <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border report-card">
            
            {/* PRINT HEADER */}
            <div className="print-header text-center border-b pb-4 mb-6">
              <h2 className="text-2xl font-black uppercase tracking-tight text-black">{storeName}</h2>
              {storeAddress && <p className="text-xs text-gray-700">{storeAddress}</p>}
              <div className="flex justify-center items-center gap-4 text-xs text-gray-700 mt-1">
                {storePhone && <span>Phone: {storePhone}</span>}
                {storePan && <span>PAN/VAT: {storePan}</span>}
              </div>
              <h3 className="text-lg font-bold uppercase tracking-wider text-black mt-3 underline">
                DAY BOOK REPORT
              </h3>
              <p className="text-xs text-gray-600 mt-0.5">
                Report Period: {filterType === 'custom' ? `${fromDate || '-'} to ${toDate || '-'}` : filters.find(f => f.key === filterType)?.value}
                {'} | Generated: '}{new Date().toLocaleString()}
              </p>
            </div>

            {/* PAYMENT TYPE COLLECTIONS SUMMARY CARDS */}
            <div className="mb-6 bg-slate-50 border border-slate-200 rounded-2xl p-4 print:bg-transparent print:border-gray-300">
              <div className="flex items-center justify-between mb-3 border-b border-slate-200 pb-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                  <IconWallet size={16} className="text-emerald-600" /> Collections By Payment Type
                </h3>
                <span className="text-xs text-slate-600">
                  Total Collected: <b className="text-emerald-700 font-bold">{currency}{getSalesTotal().toFixed(2)}</b>
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {(report.sales_payments || []).map((sp, idx) => {
                  const hasAdjustment = (Number(sp.transfers_in) || 0) > 0 || (Number(sp.transfers_out) || 0) > 0 || (Number(sp.journal_in) || 0) > 0 || (Number(sp.journal_out) || 0) > 0 || (Number(sp.expenses_paid) || 0) > 0 || (Number(sp.purchases_paid) || 0) > 0;
                  return (
                    <div key={idx} className="bg-white border border-slate-200 rounded-xl p-3 shadow-xs flex flex-col justify-between hover:border-emerald-400 transition-colors">
                      <div className="flex items-center justify-between gap-1 text-xs text-gray-500">
                        <span className="font-semibold truncate text-gray-700">{sp.payment_method}</span>
                        <span className="text-emerald-600 shrink-0 w-4 h-4 flex items-center justify-center">
                          {PAYMENT_ICONS[sp.icon] || <IconCash size={16} />}
                        </span>
                      </div>
                      <div className="mt-2">
                        <div className="text-[11px] text-gray-400 font-medium">Net In-Hand</div>
                        <div className="text-base font-black text-gray-900">
                          {currency}{parseFloat(hasAdjustment ? (sp.net_amount ?? sp.total_amount) : (sp.total_amount || 0)).toFixed(2)}
                        </div>
                        {hasAdjustment && (
                          <div className="text-[10px] text-gray-500 mt-1 pt-1 border-t border-dashed border-gray-200 space-y-0.5">
                            <div>Sales: <span className="font-semibold text-gray-700">{currency}{parseFloat(sp.total_amount || 0).toFixed(2)}</span></div>
                            {Number(sp.transfers_in) > 0 && <div className="text-blue-600 font-medium">+Transfer: {currency}{parseFloat(sp.transfers_in).toFixed(2)}</div>}
                            {Number(sp.transfers_out) > 0 && <div className="text-rose-600 font-medium">-Transfer: {currency}{parseFloat(sp.transfers_out).toFixed(2)}</div>}
                            {Number(sp.journal_in) > 0 && <div className="text-indigo-600 font-medium">+Journal: {currency}{parseFloat(sp.journal_in).toFixed(2)}</div>}
                            {Number(sp.journal_out) > 0 && <div className="text-orange-600 font-medium">-Journal: {currency}{parseFloat(sp.journal_out).toFixed(2)}</div>}
                            {Number(sp.expenses_paid) > 0 && <div className="text-rose-500 font-medium">-Expense: {currency}{parseFloat(sp.expenses_paid).toFixed(2)}</div>}
                            {Number(sp.purchases_paid) > 0 && <div className="text-rose-500 font-medium">-Purchase: {currency}{parseFloat(sp.purchases_paid).toFixed(2)}</div>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* SALES SUMMARY GRID */}
            {report.invoiceSummary && (
              <div className="mb-8">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
                  <IconReceipt size={16} className="text-indigo-600" /> Sales & Billing Breakdown
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-xs print:border-gray-400">
                    <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">Gross Sales</div>
                    <div className="text-lg font-black text-gray-900">{currency}{parseFloat(report.invoiceSummary.total_gross || 0).toFixed(2)}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">Pre-discount items</div>
                  </div>

                  <div className="bg-white p-3.5 rounded-xl border border-rose-200 shadow-xs print:border-gray-400">
                    <div className="text-[11px] font-bold text-rose-600 uppercase tracking-wider mb-1">Total Discount</div>
                    <div className="text-lg font-black text-rose-600">-{currency}{parseFloat(report.invoiceSummary.total_discount || 0).toFixed(2)}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">Concessions allowed</div>
                  </div>

                  <div className="bg-white p-3.5 rounded-xl border border-blue-200 shadow-xs print:border-gray-400">
                    <div className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-1">VAT / Taxes</div>
                    <div className="text-lg font-black text-blue-700">+{currency}{parseFloat(report.invoiceSummary.total_tax || 0).toFixed(2)}</div>
                    <div className="text-[10px] text-gray-400 mt-0.5">Collected tax</div>
                  </div>

                  <div className="bg-white p-3.5 rounded-xl border border-purple-200 shadow-xs print:border-gray-400">
                    <div className="text-[11px] font-bold text-purple-600 uppercase tracking-wider mb-1">Round Off</div>
                    <div className="text-lg font-black text-purple-700">
                      {parseFloat(report.invoiceSummary.total_round_off || 0) >= 0 ? '+' : ''}{currency}{parseFloat(report.invoiceSummary.total_round_off || 0).toFixed(2)}
                    </div>
                    <div className="text-[10px] text-gray-400 mt-0.5">Bill adjustments</div>
                  </div>

                  <div className="bg-emerald-50/70 p-3.5 rounded-xl border border-emerald-300 shadow-xs print:bg-white print:border-gray-400">
                    <div className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider mb-1">Net Sales</div>
                    <div className="text-lg font-black text-emerald-700 print:text-black">{currency}{parseFloat(report.invoiceSummary.net_sales || 0).toFixed(2)}</div>
                    <div className="text-[10px] text-emerald-600 mt-0.5">Total billed invoices</div>
                  </div>

                  <div className="bg-amber-50/70 p-3.5 rounded-xl border border-amber-300 shadow-xs print:bg-white print:border-gray-400">
                    <div className="text-[11px] font-bold text-amber-800 uppercase tracking-wider mb-1">Sales Due (Credit)</div>
                    <div className="text-lg font-black text-amber-700 print:text-black">
                      {currency}{parseFloat(report.total_sales_due ?? report.invoiceSummary.total_due ?? 0).toFixed(2)}
                    </div>
                    <div className="text-[10px] text-amber-700 mt-0.5">Receivable from guests</div>
                  </div>
                </div>

                {/* Additional notes row if service charge or complementary exist */}
                {(Number(report.invoiceSummary.total_complementary || 0) > 0 || Number(report.invoiceSummary.total_service_charge || 0) > 0) && (
                  <div className="flex flex-wrap gap-4 mt-2 px-1 text-xs text-gray-500">
                    {Number(report.invoiceSummary.total_complementary || 0) > 0 && (
                      <span className="flex items-center gap-1">
                        🎁 <b>Complementary Value:</b> {currency}{parseFloat(report.invoiceSummary.total_complementary).toFixed(2)}
                      </span>
                    )}
                    {Number(report.invoiceSummary.total_service_charge || 0) > 0 && (
                      <span className="flex items-center gap-1">
                        🍽️ <b>Service Charge:</b> {currency}{parseFloat(report.invoiceSummary.total_service_charge).toFixed(2)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* CASH IN & CASH OUT GRIDS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Cash In Table */}
              <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100 print:bg-transparent print:border-gray-300">
                <h3 className="text-emerald-800 print:text-black font-bold mb-3 flex items-center gap-2">
                  <IconArrowDownRight size={20} className="text-emerald-600 print:hidden"/> CASH IN (RECEIPTS)
                </h3>
                <table className="table table-sm w-full border-collapse">
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 text-gray-700 font-medium">Opening Balance</td>
                      <td className="py-2 text-right font-bold text-gray-900">{currency}{parseFloat(openingBalance || 0).toFixed(2)}</td>
                    </tr>
                    {(report.sales_payments || []).map((sp, idx) => (
                      <tr key={idx} className="border-b hover:bg-emerald-50/40 transition-colors">
                        <td className="py-2 text-gray-700 flex items-center gap-2">
                          <span className="w-4 h-4 flex items-center justify-center text-emerald-600">
                            {PAYMENT_ICONS[sp.icon] || <IconCash size={16} />}
                          </span>
                          <span>Collection ({sp.payment_method || 'Cash'})</span>
                        </td>
                        <td className="py-2 text-right font-semibold text-gray-900">{currency}{parseFloat(sp.total_amount || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                    {parseFloat(report.due_collected || 0) > 0 && (
                      <tr className="border-b bg-amber-50/40 text-xs">
                        <td className="py-1.5 text-gray-600 pl-6 italic">↳ Of which Past Due / Credit Collected</td>
                        <td className="py-1.5 text-right font-medium text-gray-700 italic">({currency}{parseFloat(report.due_collected).toFixed(2)})</td>
                      </tr>
                    )}
                    {/* Fund Transfers In (Contra) */}
                    {parseFloat(report.total_transfers_in || 0) > 0 && (
                      <tr className="border-b bg-blue-50/40">
                        <td className="py-2 text-blue-900 font-semibold flex items-center gap-2">
                          <IconRefresh size={16} className="text-blue-600" />
                          <span>Fund Transfers In (Contra)</span>
                        </td>
                        <td className="py-2 text-right font-bold text-blue-800">+{currency}{parseFloat(report.total_transfers_in).toFixed(2)}</td>
                      </tr>
                    )}
                    {(report.fund_transfers || []).map((ft, idx) => (
                      <tr key={`ft-in-${idx}`} className="border-b bg-blue-50/20 text-xs">
                        <td className="py-1.5 text-gray-600 pl-6">
                          <span>↳ Received into <b>{ft.to_title || 'Bank'}</b> from {ft.from_title || 'Cash'}</span>
                          {ft.reference ? <span className="text-gray-400 italic"> ({ft.reference})</span> : ''}
                        </td>
                        <td className="py-1.5 text-right font-medium text-blue-700">+{currency}{parseFloat(ft.amount || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                    {/* Manual Journal Inflows */}
                    {parseFloat(report.total_journal_in || 0) > 0 && (
                      <tr className="border-b bg-indigo-50/40">
                        <td className="py-2 text-indigo-900 font-semibold flex items-center gap-2">
                          <IconFileText size={16} className="text-indigo-600" />
                          <span>Journal Inflow Adjustments</span>
                        </td>
                        <td className="py-2 text-right font-bold text-indigo-800">+{currency}{parseFloat(report.total_journal_in).toFixed(2)}</td>
                      </tr>
                    )}
                    {(report.journal_adjustments || []).filter(j => parseFloat(j.debit || 0) > 0).map((j, idx) => (
                      <tr key={`j-in-${idx}`} className="border-b bg-indigo-50/20 text-xs">
                        <td className="py-1.5 text-gray-600 pl-6">
                          <span>↳ Debit <b>{j.account}</b>: {j.description || 'Adjustment'}</span>
                        </td>
                        <td className="py-1.5 text-right font-medium text-indigo-700">+{currency}{parseFloat(j.debit || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-emerald-300 print:border-black font-bold">
                      <td className="pt-3 text-base text-emerald-900 print:text-black">TOTAL CASH IN</td>
                      <td className="pt-3 text-right text-base text-emerald-700 print:text-black">{currency}{totalCashIn.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Cash Out Table */}
              <div className="bg-rose-50/50 p-4 rounded-xl border border-rose-100 print:bg-transparent print:border-gray-300">
                <h3 className="text-rose-800 print:text-black font-bold mb-3 flex items-center gap-2">
                  <IconArrowUpRight size={20} className="text-rose-600 print:hidden"/> CASH OUT (PAYMENTS)
                </h3>
                <table className="table table-sm w-full border-collapse">
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 text-gray-700 font-semibold">General & Store Expenses (Paid)</td>
                      <td className="py-2 text-right font-semibold text-gray-900">{currency}{parseFloat(report.total_expenses || 0).toFixed(2)}</td>
                    </tr>
                    {/* Itemized Paid Expenses under Cash Out */}
                    {(report.expenses || []).filter(exp => exp.payment_status !== 'due' && String(exp.payment_method).toLowerCase() !== 'due').map((exp, idx) => (
                      <tr key={`exp-${idx}`} className="border-b bg-rose-50/20 text-xs">
                        <td className="py-1.5 text-gray-600 pl-4">
                          <span className="font-semibold text-gray-800">{exp.category_name || 'Expense'}</span>
                          {exp.supplier_name ? <span className="text-gray-500 font-medium"> ({exp.supplier_name})</span> : ''}
                          {exp.description ? ` - ${exp.description}` : ''}
                          <span className="text-gray-400 ml-1">[{exp.payment_method || 'Cash'}]</span>
                        </td>
                        <td className="py-1.5 text-right font-medium text-gray-800">{currency}{parseFloat(exp.amount || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                    {(report.purchase_payments || []).map((pp, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="py-2 text-gray-700">Purchases Paid ({pp.payment_method || 'Cash'})</td>
                        <td className="py-2 text-right font-semibold text-gray-900">{currency}{parseFloat(pp.total_amount).toFixed(2)}</td>
                      </tr>
                    ))}
                    {parseFloat(report.due_paid || 0) > 0 && (
                      <tr className="border-b bg-amber-50/40 text-xs">
                        <td className="py-1.5 text-gray-600 pl-6 italic">↳ Of which Past Supplier Due Paid</td>
                        <td className="py-1.5 text-right font-medium text-gray-700 italic">({currency}{parseFloat(report.due_paid).toFixed(2)})</td>
                      </tr>
                    )}
                    {parseFloat(report.total_expense_due || 0) > 0 && (
                      <tr className="border-b bg-amber-50/40 text-xs">
                        <td className="py-1.5 text-amber-800 pl-4 font-semibold italic">↳ New Expense Due (Unpaid / Payable)</td>
                        <td className="py-1.5 text-right font-bold text-amber-800 italic">({currency}{parseFloat(report.total_expense_due).toFixed(2)})</td>
                      </tr>
                    )}
                    {/* Fund Transfers Out (Contra) */}
                    {parseFloat(report.total_transfers_out || 0) > 0 && (
                      <tr className="border-b bg-rose-50/40">
                        <td className="py-2 text-rose-900 font-semibold flex items-center gap-2">
                          <IconRefresh size={16} className="text-rose-600" />
                          <span>Fund Transfers Out (Contra)</span>
                        </td>
                        <td className="py-2 text-right font-bold text-rose-800">+{currency}{parseFloat(report.total_transfers_out).toFixed(2)}</td>
                      </tr>
                    )}
                    {(report.fund_transfers || []).map((ft, idx) => (
                      <tr key={`ft-out-${idx}`} className="border-b bg-rose-50/20 text-xs">
                        <td className="py-1.5 text-gray-600 pl-6">
                          <span>↳ Transferred from <b>{ft.from_title || 'Cash'}</b> to {ft.to_title || 'Bank'}</span>
                          {ft.reference ? <span className="text-gray-400 italic"> ({ft.reference})</span> : ''}
                        </td>
                        <td className="py-1.5 text-right font-medium text-rose-700">-{currency}{parseFloat(ft.amount || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                    {/* Manual Journal Outflows */}
                    {parseFloat(report.total_journal_out || 0) > 0 && (
                      <tr className="border-b bg-orange-50/40">
                        <td className="py-2 text-orange-900 font-semibold flex items-center gap-2">
                          <IconFileText size={16} className="text-orange-600" />
                          <span>Journal Outflow Adjustments</span>
                        </td>
                        <td className="py-2 text-right font-bold text-orange-800">+{currency}{parseFloat(report.total_journal_out).toFixed(2)}</td>
                      </tr>
                    )}
                    {(report.journal_adjustments || []).filter(j => parseFloat(j.credit || 0) > 0).map((j, idx) => (
                      <tr key={`j-out-${idx}`} className="border-b bg-orange-50/20 text-xs">
                        <td className="py-1.5 text-gray-600 pl-6">
                          <span>↳ Credit <b>{j.account}</b>: {j.description || 'Adjustment'}</span>
                        </td>
                        <td className="py-1.5 text-right font-medium text-orange-700">-{currency}{parseFloat(j.credit || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-rose-300 print:border-black font-bold">
                      <td className="pt-3 text-base text-rose-900 print:text-black">TOTAL CASH OUT</td>
                      <td className="pt-3 text-right text-base text-rose-700 print:text-black">{currency}{totalCashOut.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* DAY BOOK SUMMARY STATS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-gray-50 print:bg-white p-4 rounded-xl border print:border-gray-300">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Net Closing Balance</div>
                <div className="text-2xl font-black text-emerald-700 print:text-black mt-1">
                  {currency}{calcClosingBalance.toFixed(2)}
                </div>
                <div className="text-xs text-gray-400 mt-1">Total In ({currency}{totalCashIn.toFixed(2)}) - Total Out ({currency}{totalCashOut.toFixed(2)})</div>
              </div>

              <div className="bg-gray-50 print:bg-white p-4 rounded-xl border print:border-gray-300">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">New Sales Credit</div>
                <div className="text-2xl font-bold text-amber-600 print:text-black mt-1">
                  {currency}{parseFloat(report.total_sales_due || 0).toFixed(2)}
                </div>
                <div className="text-xs text-gray-400 mt-1">Customer receivables</div>
              </div>

              <div className="bg-gray-50 print:bg-white p-4 rounded-xl border print:border-gray-300">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">New Purchase Credit</div>
                <div className="text-2xl font-bold text-rose-600 print:text-black mt-1">
                  {currency}{parseFloat(report.total_purchase_due || 0).toFixed(2)}
                </div>
                <div className="text-xs text-gray-400 mt-1">Purchase payables</div>
              </div>

              <div className="bg-gray-50 print:bg-white p-4 rounded-xl border print:border-gray-300">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">New Expense Due</div>
                <div className="text-2xl font-bold text-amber-700 print:text-black mt-1">
                  {currency}{parseFloat(report.total_expense_due || 0).toFixed(2)}
                </div>
                <div className="text-xs text-gray-400 mt-1">Expense payables to suppliers</div>
              </div>
            </div>

            {/* ITEMIZED EXPENSES BREAKDOWN */}
            {report.expenses && report.expenses.length > 0 && (
              <div className="mb-8 bg-white dark:bg-restro-gray p-4 rounded-xl border border-gray-200 dark:border-gray-800 print:border-gray-300">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-base font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    <IconFileText size={18} className="text-gray-500" />
                    Itemized Expenses Breakdown ({report.expenses.length})
                  </h3>
                  <span className="text-xs text-gray-500">All recorded expenses for selected period</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="table table-sm w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs">
                        <th>#</th>
                        <th>Category</th>
                        <th>Supplier / Payee</th>
                        <th>Description</th>
                        <th>Method</th>
                        <th>Status</th>
                        <th className="text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.expenses.map((exp, idx) => {
                        const isDue = exp.payment_status === 'due' || String(exp.payment_method).toLowerCase() === 'due';
                        return (
                          <tr key={exp.id || idx} className={`border-b text-xs ${isDue ? 'bg-amber-50/30 dark:bg-amber-950/10' : ''}`}>
                            <td className="text-gray-500">#{exp.id}</td>
                            <td className="font-semibold text-gray-800 dark:text-gray-200">{exp.category_name || 'General Expense'}</td>
                            <td className="text-gray-700 dark:text-gray-300">{exp.supplier_name || '-'}</td>
                            <td className="max-w-xs truncate text-gray-600 dark:text-gray-400" title={exp.description}>{exp.description || '-'}</td>
                            <td>
                              <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 font-medium">
                                {exp.payment_method || 'Cash'}
                              </span>
                            </td>
                            <td>
                              {isDue ? (
                                <span className="badge badge-warning badge-xs font-bold text-amber-900 bg-amber-200">Due</span>
                              ) : (
                                <span className="badge badge-success badge-xs font-bold text-emerald-900 bg-emerald-200">Paid</span>
                              )}
                            </td>
                            <td className={`text-right font-bold ${isDue ? 'text-amber-700' : 'text-gray-900 dark:text-gray-100'}`}>
                              {currency}{parseFloat(exp.amount || 0).toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {/* FUND TRANSFERS & CONTRA TABLE */}
            {report.fund_transfers && report.fund_transfers.length > 0 && (
              <div className="mb-8 bg-white dark:bg-restro-gray p-4 rounded-xl border border-gray-200 dark:border-gray-800 print:border-gray-300">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-base font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    <IconRefresh size={18} className="text-blue-600" />
                    Fund Transfers & Contra Entries ({report.fund_transfers.length})
                  </h3>
                  <span className="text-xs text-gray-500">Transfers between cash register, banks, and payment accounts</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="table table-sm w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs">
                        <th>#</th>
                        <th>Date & Time</th>
                        <th>From Account</th>
                        <th>To Account</th>
                        <th>Reference / Purpose</th>
                        <th>Notes</th>
                        <th className="text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.fund_transfers.map((ft, idx) => (
                        <tr key={ft.id || idx} className="border-b text-xs hover:bg-slate-50">
                          <td className="text-gray-500">#{ft.id}</td>
                          <td className="text-gray-600">{formatDateTime(ft.transfer_date)}</td>
                          <td className="font-semibold text-rose-700">
                            <span className="inline-flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                              {ft.from_title || 'Cash'}
                            </span>
                          </td>
                          <td className="font-semibold text-emerald-700">
                            <span className="inline-flex items-center gap-1.5">
                              <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                              {ft.to_title || 'Bank'}
                            </span>
                          </td>
                          <td className="text-gray-700 font-medium">{ft.reference || '-'}</td>
                          <td className="text-gray-500 italic">{ft.notes || '-'}</td>
                          <td className="text-right font-black text-gray-900">
                            {currency}{parseFloat(ft.amount || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* MANUAL JOURNAL ADJUSTMENTS TABLE */}
            {report.journal_adjustments && report.journal_adjustments.length > 0 && (
              <div className="mb-8 bg-white dark:bg-restro-gray p-4 rounded-xl border border-gray-200 dark:border-gray-800 print:border-gray-300">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-base font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    <IconFileText size={18} className="text-indigo-600" />
                    General Journal Adjustments ({report.journal_adjustments.length})
                  </h3>
                  <span className="text-xs text-gray-500">Manual adjustments affecting ledger accounts and dues</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="table table-sm w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs">
                        <th>#</th>
                        <th>Date & Time</th>
                        <th>Account</th>
                        <th>Description</th>
                        <th className="text-right">Debit (In / Due)</th>
                        <th className="text-right">Credit (Out / Paid)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.journal_adjustments.map((j, idx) => (
                        <tr key={j.id || idx} className="border-b text-xs hover:bg-slate-50">
                          <td className="text-gray-500">#{j.id}</td>
                          <td className="text-gray-600">{formatDateTime(j.entry_date)}</td>
                          <td className="font-bold text-gray-800">{j.account}</td>
                          <td className="text-gray-600">{j.description || '-'}</td>
                          <td className="text-right font-bold text-indigo-700">
                            {parseFloat(j.debit || 0) > 0 ? `${currency}${parseFloat(j.debit).toFixed(2)}` : '-'}
                          </td>
                          <td className="text-right font-bold text-rose-700">
                            {parseFloat(j.credit || 0) > 0 ? `${currency}${parseFloat(j.credit).toFixed(2)}` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 3-COLUMN PROFESSIONAL SIGNATURE BLOCK */}
            <div className="signature-section mt-12 pt-8 border-t border-gray-200">
              <div className="grid grid-cols-3 gap-8 text-center">
                <div className="flex flex-col items-center">
                  <div className="w-40 border-b-2 border-dashed border-gray-400 h-12 mb-2"></div>
                  <span className="font-bold text-xs uppercase tracking-wider text-gray-800">Prepared By</span>
                  <span className="text-[11px] text-gray-500">Cashier / Staff</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-40 border-b-2 border-dashed border-gray-400 h-12 mb-2"></div>
                  <span className="font-bold text-xs uppercase tracking-wider text-gray-800">Verified By</span>
                  <span className="text-[11px] text-gray-500">Supervisor / Accountant</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-40 border-b-2 border-dashed border-gray-400 h-12 mb-2"></div>
                  <span className="font-bold text-xs uppercase tracking-wider text-gray-800">Approved By</span>
                  <span className="text-[11px] text-gray-500">Manager / Owner</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* START SHIFT MODAL */}
      <dialog 
        open={isStartShiftModalOpen} 
        onKeyDown={(e) => { if (e.key === 'Escape') setIsStartShiftModalOpen(false); }}
        className={`modal ${isStartShiftModalOpen ? 'modal-open' : ''}`}
      >
        <div className="modal-box max-w-2xl border border-emerald-200 rounded-3xl p-0 shadow-2xl bg-white flex flex-col max-h-[90vh]">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-5 text-white shrink-0">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-black text-xl flex items-center gap-2">
                  <IconLockOpen size={24} /> Start Cashier Register Shift
                </h3>
                <p className="text-xs text-emerald-100 mt-0.5">Open cash drawer & declare initial float & denomination count</p>
              </div>
              <button 
                type="button"
                onClick={() => setIsStartShiftModalOpen(false)}
                className="btn btn-sm btn-circle btn-ghost text-white"
              >
                ✕
              </button>
            </div>
          </div>

          <form onSubmit={handleStartShiftSubmit} className="p-6 space-y-5 bg-white flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Cashier / Staff
                </label>
                <input 
                  type="text" 
                  disabled 
                  value={currentUser?.name || currentUser?.email || 'Logged-in Cashier'} 
                  className="input input-sm input-bordered w-full bg-gray-100 text-gray-800 font-semibold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Starting Cash Float Amount ({currency}) *
                </label>
                <input 
                  type="number" 
                  step="0.01" 
                  required
                  autoFocus
                  placeholder="0.00" 
                  value={startFloat} 
                  onChange={e => setStartFloat(e.target.value)} 
                  className="input input-sm input-bordered w-full font-bold text-lg text-emerald-800 border-gray-300 focus:border-emerald-600"
                />
              </div>
            </div>

            {/* START SHIFT DENOMINATION COUNTER */}
            {currenciesList && currenciesList.length > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-2.5">
                  <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                    <IconCoins size={18} className="text-amber-600" /> Opening Cash Denomination Breakdown (Optional)
                  </span>
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200">
                    Calculated Sum: {currency}{startFloat}
                  </span>
                </div>

                {currenciesList.map((c) => (
                  <div key={c.code} className="space-y-2">
                    <div className="text-xs font-bold text-gray-700 flex justify-between items-center bg-slate-100 px-2.5 py-1 rounded-md">
                      <span>{c.name} ({c.symbol})</span>
                      <span className="text-[11px] font-normal text-gray-500">Exchange Rate: {c.exchange_rate}</span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {(() => {
                        let denoms = c.denominations || [];
                        if (typeof denoms === 'string') {
                          try { denoms = JSON.parse(denoms); } catch(e) { denoms = []; }
                        }
                        if (!Array.isArray(denoms)) denoms = [];
                        return denoms.map((denom) => {
                          const val = startDenominationCounts[c.code]?.[denom] ?? '';
                          const lineTotal = Number(denom) * (parseFloat(val) || 0);

                          return (
                            <div 
                              key={denom} 
                              className={`p-2 rounded-xl border transition-all ${
                                Number(val) > 0
                                  ? 'border-amber-400 bg-amber-50/60 shadow-2xs'
                                  : 'border-slate-200 bg-white hover:border-slate-300'
                              }`}
                            >
                              <div className="flex justify-between items-center mb-1 text-xs">
                                <span className="font-extrabold text-slate-800">
                                  {c.symbol}{denom}
                                </span>
                                <span className="text-[10px] font-bold text-slate-500">
                                  = {c.symbol}{lineTotal.toLocaleString()}
                                </span>
                              </div>

                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-400 font-bold px-0.5">×</span>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                placeholder="0"
                                className="input input-xs input-bordered w-full text-center font-bold text-slate-900 h-8 text-xs bg-white border-gray-300 focus:border-amber-500"
                                value={val}
                                onChange={(e) => {
                                  const cnt = e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0);
                                  const newCounts = {
                                    ...startDenominationCounts,
                                    [c.code]: {
                                      ...(startDenominationCounts[c.code] || {}),
                                      [denom]: cnt
                                    }
                                  };
                                  setStartDenominationCounts(newCounts);
                                  let sum = 0;
                                  currenciesList.forEach((cur) => {
                                    const counts = newCounts[cur.code] || {};
                                    const rate = parseFloat(exchangeRatesState[cur.code] ?? cur.exchange_rate ?? 1.0);
                                    let curDenoms = cur.denominations || [];
                                    if (typeof curDenoms === 'string') {
                                      try { curDenoms = JSON.parse(curDenoms); } catch(e) { curDenoms = []; }
                                    }
                                    if (!Array.isArray(curDenoms)) curDenoms = [];
                                    curDenoms.forEach((d) => {
                                      sum += Number(d) * (parseFloat(counts[d]) || 0) * rate;
                                    });
                                  });
                                  if (sum > 0) setStartFloat(sum.toFixed(2));
                                }}
                              />
                            </div>
                          </div>
                        );
                        });
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                Opening Shift Notes (Optional)
              </label>
              <textarea
                rows={2}
                placeholder="e.g. Morning counter shift, drawer #1, initial change provided by manager"
                value={startNote}
                onChange={e => setStartNote(e.target.value)}
                className="textarea textarea-bordered w-full text-xs text-gray-800"
              ></textarea>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t">
              <button 
                type="button" 
                onClick={() => setIsStartShiftModalOpen(false)} 
                className="btn btn-sm btn-ghost text-gray-600"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={startingShiftLoading}
                className="btn btn-sm bg-emerald-600 hover:bg-emerald-700 text-white border-none gap-1.5 px-5 shadow-sm"
              >
                {startingShiftLoading ? <span className="loading loading-spinner loading-xs"></span> : <IconCheck size={16} />}
                Confirm & Start Shift
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* OPENING BALANCE DENOMINATION COUNTER MODAL */}
      <dialog 
        open={isOBDenomModalOpen} 
        onKeyDown={(e) => { if (e.key === 'Escape') setIsOBDenomModalOpen(false); }}
        className={`modal ${isOBDenomModalOpen ? 'modal-open' : ''}`}
      >
        <div className="modal-box max-w-xl border border-amber-200 rounded-3xl p-0 shadow-2xl bg-white flex flex-col max-h-[90vh]">
          <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-5 text-white flex justify-between items-center shrink-0">
            <h3 className="font-extrabold text-lg flex items-center gap-2">
              <IconCoins size={20} /> Opening Balance Cash Denominations
            </h3>
            <button 
              type="button"
              onClick={() => setIsOBDenomModalOpen(false)}
              className="btn btn-sm btn-circle btn-ghost text-white"
            >
              ✕
            </button>
          </div>

          <div className="p-5 space-y-4 flex-1 overflow-y-auto">
            {currenciesList && currenciesList.length > 0 ? (
              currenciesList.map((c) => (
                <div key={c.code} className="bg-slate-50 border rounded-2xl p-3 space-y-2">
                  <div className="text-xs font-bold text-gray-800 flex justify-between border-b pb-1">
                    <span>{c.name} ({c.symbol})</span>
                    <span>Rate: {c.exchange_rate}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {(() => {
                      let denoms = c.denominations || [];
                      if (typeof denoms === 'string') {
                        try { denoms = JSON.parse(denoms); } catch(e) { denoms = []; }
                      }
                      if (!Array.isArray(denoms)) denoms = [];
                      return denoms.map((denom) => {
                        const val = obDenominationCounts[c.code]?.[denom] ?? '';
                      return (
                        <div key={denom} className="flex items-center gap-1 bg-white border px-2 py-1.5 rounded text-xs shadow-2xs">
                          <span className="w-14 font-semibold text-gray-600">{c.symbol}{denom}</span>
                          <span className="text-gray-400">×</span>
                          <input
                            type="number"
                            min="0"
                            placeholder="0"
                            className="input input-xs input-bordered w-full font-bold text-right"
                            value={val}
                            onChange={(e) => {
                              const cnt = e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0);
                              const newCounts = {
                                ...obDenominationCounts,
                                [c.code]: {
                                  ...(obDenominationCounts[c.code] || {}),
                                  [denom]: cnt
                                }
                              };
                              setObDenominationCounts(newCounts);
                              let sum = 0;
                              currenciesList.forEach((cur) => {
                                const counts = newCounts[cur.code] || {};
                                const rate = parseFloat(exchangeRatesState[cur.code] ?? cur.exchange_rate ?? 1.0);
                                let curDenoms = cur.denominations || [];
                                if (typeof curDenoms === 'string') {
                                  try { curDenoms = JSON.parse(curDenoms); } catch(e) { curDenoms = []; }
                                }
                                if (!Array.isArray(curDenoms)) curDenoms = [];
                                curDenoms.forEach((d) => {
                                  sum += Number(d) * (parseFloat(counts[d]) || 0) * rate;
                                });
                              });
                              if (sum > 0) setOpeningBalance(sum.toFixed(2));
                            }}
                          />
                        </div>
                      );
                      });
                    })()}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-500 text-center py-4">No currency denomination config found.</p>
            )}

            <div className="flex justify-between items-center pt-2 border-t">
              <span className="text-xs font-bold text-gray-700">
                Opening Balance Sum: <strong className="text-emerald-700">{currency}{openingBalance}</strong>
              </span>
              <button 
                type="button" 
                onClick={() => setIsOBDenomModalOpen(false)} 
                className="btn btn-sm btn-primary px-4"
              >
                Apply & Close
              </button>
            </div>
          </div>
        </div>
      </dialog>

      {/* END SHIFT / REGISTER CLOSING MODAL */}
      <dialog 
        open={isEndShiftModalOpen} 
        onKeyDown={(e) => { if (e.key === 'Escape') setIsEndShiftModalOpen(false); }}
        className={`modal ${isEndShiftModalOpen ? 'modal-open' : ''}`}
      >
        <div className="modal-box max-w-4xl border border-rose-200 rounded-3xl p-0 shadow-2xl bg-white flex flex-col max-h-[90vh]">
          <div className="bg-gradient-to-r from-rose-600 to-rose-700 p-6 text-white shrink-0">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-black text-xl flex items-center gap-2">
                  <IconLock size={24} /> End Shift & Close Register
                </h3>
                <p className="text-xs text-rose-100 mt-0.5">
                  Shift #{currentShift?.shift_number} | Opened: {formatDateTime(currentShift?.opened_at)} by {currentShift?.opened_by_name || 'Cashier'}
                </p>
              </div>
              <button 
                type="button"
                onClick={() => setIsEndShiftModalOpen(false)}
                className="btn btn-sm btn-circle btn-ghost text-white"
              >
                ✕
              </button>
            </div>
          </div>

          <form onSubmit={handleEndShiftSubmit} className="p-6 space-y-5 bg-white flex-1 overflow-y-auto">
            {/* System computed stats summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <div className="bg-gray-50 border rounded-xl p-2.5">
                <div className="text-[11px] text-gray-500 font-medium">Opening Float</div>
                <div className="text-sm font-bold text-gray-800">{currency}{currentFloat.toFixed(2)}</div>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-2.5">
                <div className="text-[11px] text-emerald-700 font-medium">System Cash Sales</div>
                <div className="text-sm font-bold text-emerald-800">{currency}{parseFloat(shiftStats.cash_sales || 0).toFixed(2)}</div>
              </div>
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-2.5">
                <div className="text-[11px] text-blue-700 font-medium">System QR Sales</div>
                <div className="text-sm font-bold text-blue-800">{currency}{parseFloat(shiftStats.qr_sales || 0).toFixed(2)}</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5">
                <div className="text-[11px] text-amber-800 font-bold">Expected Cash</div>
                <div className="text-sm font-extrabold text-amber-900">{currency}{currentExpectedCash.toFixed(2)}</div>
              </div>
            </div>

            {shiftStats.payment_breakdown && shiftStats.payment_breakdown.length > 0 && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Shift Collections By Payment Method</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {shiftStats.payment_breakdown.map((pb, idx) => (
                    <div key={idx} className="bg-white border border-slate-200 rounded-lg p-2 flex items-center justify-between shadow-2xs">
                      <div className="flex items-center gap-1.5 overflow-hidden">
                        <span className="text-emerald-600 shrink-0 w-3.5 h-3.5 flex items-center justify-center">
                          {PAYMENT_ICONS[pb.icon] || <IconCash size={14} />}
                        </span>
                        <span className="text-xs text-slate-700 truncate font-medium">{pb.payment_method}</span>
                      </div>
                      <span className="text-xs font-bold text-slate-900 ml-1 shrink-0">{currency}{parseFloat(pb.total_amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CASH DENOMINATION COUNTER & MULTI-CURRENCY EXCHANGE SECTION */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-2.5">
                <div className="flex items-center gap-2">
                  <span className="p-1.5 bg-amber-100 text-amber-800 rounded-lg">
                    <IconCoins size={18} />
                  </span>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center gap-2">
                      Cash Denomination Counter
                      <span className="badge badge-xs bg-amber-200 text-amber-900 border-none font-semibold">Multi-Currency</span>
                    </h4>
                    <p className="text-[11px] text-slate-500">
                      Count notes & coins per currency. Converted values sync to Drawer Count.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="bg-white border border-slate-300 rounded-xl px-3 py-1 text-right shadow-2xs">
                    <span className="text-[10px] text-slate-400 font-bold block uppercase leading-none">Total Counted</span>
                    <span className="text-sm font-black text-slate-900">
                      {currency}{grandBaseTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleApplyDenominationsToActualCash}
                    className="btn btn-xs bg-amber-600 hover:bg-amber-700 text-white border-none gap-1 px-2.5 shadow-xs"
                    title="Copy counted denomination sum to Actual Cash In Hand"
                  >
                    <IconCheck size={14} /> Apply to Drawer
                  </button>
                </div>
              </div>

              {/* Currency Tabs */}
              {currenciesList && currenciesList.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {currenciesList.map((currItem) => {
                      const isSelected = selectedCurrencyCode === currItem.code;
                      const currTotals = perCurrencyTotals[currItem.code];
                      const hasCount = (currTotals?.currencyTotal || 0) > 0;
                      return (
                        <button
                          key={currItem.code}
                          type="button"
                          onClick={() => setSelectedCurrencyCode(currItem.code)}
                          className={`btn btn-xs rounded-xl font-bold transition-all ${
                            isSelected
                              ? 'bg-slate-900 text-white shadow-xs'
                              : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
                          }`}
                        >
                          <span>{currItem.code}</span>
                          <span className="text-[10px] opacity-75">({currItem.symbol})</span>
                          {currItem.is_base ? (
                            <span className="badge badge-xs bg-emerald-500 text-white ml-0.5 border-none">Base</span>
                          ) : null}
                          {hasCount && (
                            <span className={`badge badge-xs ml-1 border-none ${isSelected ? 'bg-amber-400 text-slate-900' : 'bg-amber-100 text-amber-800'}`}>
                              {currItem.symbol}{currTotals.currencyTotal.toLocaleString()}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Selected Currency Controller & Denominations Grid */}
                  {(() => {
                    const activeCurr = currenciesList.find((c) => c.code === selectedCurrencyCode) || currenciesList[0];
                    if (!activeCurr) return null;

                    const isBase = !!activeCurr.is_base;
                    const currentRate = exchangeRatesState[activeCurr.code] ?? activeCurr.exchange_rate ?? 1.0;
                    const activeTotals = perCurrencyTotals[activeCurr.code] || { currencyTotal: 0, baseTotal: 0 };
                    
                    let parsedDenoms = activeCurr.denominations;
                    if (typeof parsedDenoms === 'string') {
                      try { parsedDenoms = JSON.parse(parsedDenoms); } catch(e) { parsedDenoms = null; }
                    }
                    if (!Array.isArray(parsedDenoms)) parsedDenoms = null;

                    const denoms = (parsedDenoms && parsedDenoms.length > 0)
                      ? parsedDenoms
                      : [1000, 500, 100, 50, 20, 10, 5, 2, 1];

                    return (
                      <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-3">
                        {/* Header info & rate setting */}
                        <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                          <div className="flex items-center gap-2">
                            <span className="font-extrabold text-sm text-slate-800">
                              {activeCurr.name || activeCurr.code} ({activeCurr.symbol})
                            </span>
                            {isBase ? (
                              <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                Base Currency (Rate: 1.0)
                              </span>
                            ) : (
                              <div className="flex items-center gap-1.5 text-xs text-slate-600">
                                <span className="font-medium">Exchange Rate:</span>
                                <div className="flex items-center gap-1">
                                  <span className="text-[11px] font-semibold text-slate-500">1 {activeCurr.code} =</span>
                                  <input
                                    type="number"
                                    step="0.0001"
                                    min="0"
                                    value={currentRate}
                                    onChange={(e) => handleShiftExchangeRateChange(activeCurr.code, e.target.value)}
                                    className="input input-xs input-bordered w-24 font-bold text-slate-800 bg-white"
                                  />
                                  <span className="text-[11px] font-semibold text-slate-500">{currency}</span>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Subtotal for this currency */}
                          <div className="text-right">
                            <span className="text-xs text-slate-600 font-medium mr-2">
                              Subtotal: <strong className="text-slate-900">{activeCurr.symbol}{activeTotals.currencyTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</strong>
                            </span>
                            {!isBase && (
                              <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                ≈ {currency}{activeTotals.baseTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Denomination Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                          {denoms.map((denom) => {
                            const count = denominationCounts[activeCurr.code]?.[denom] ?? '';
                            const lineTotal = Number(denom) * (Number(count) || 0);

                            return (
                              <div
                                key={denom}
                                className={`p-2 rounded-xl border transition-all ${
                                  Number(count) > 0
                                    ? 'border-amber-400 bg-amber-50/50 shadow-2xs'
                                    : 'border-slate-200 bg-white hover:border-slate-300'
                                }`}
                              >
                                <div className="flex justify-between items-center mb-1">
                                  <span className="font-extrabold text-xs text-slate-800">
                                    {activeCurr.symbol}{denom}
                                  </span>
                                  <span className="text-[10px] font-bold text-slate-500">
                                    = {activeCurr.symbol}{lineTotal.toLocaleString()}
                                  </span>
                                </div>

                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const currentCount = parseInt(count, 10) || 0;
                                      handleDenomCountChange(activeCurr.code, denom, Math.max(0, currentCount - 1));
                                    }}
                                    className="btn btn-xs btn-square btn-ghost text-slate-500 hover:bg-slate-200 h-7 w-7 min-h-0"
                                  >
                                    <IconMinus size={12} />
                                  </button>
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="0"
                                    value={count}
                                    onChange={(e) => handleDenomCountChange(activeCurr.code, denom, e.target.value)}
                                    className="input input-xs input-bordered w-full text-center font-bold text-slate-800 h-7 text-xs bg-white focus:border-amber-500"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const currentCount = parseInt(count, 10) || 0;
                                      handleDenomCountChange(activeCurr.code, denom, currentCount + 1);
                                    }}
                                    className="btn btn-xs btn-square btn-ghost text-slate-500 hover:bg-slate-200 h-7 w-7 min-h-0"
                                  >
                                    <IconPlus size={12} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center py-3 text-xs text-slate-400">
                  Loading currencies & denominations...
                </div>
              )}
            </div>

            {/* Reconciliation Inputs without overlapping currency symbol inside input */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Actual Cash In Hand (Drawer Count) *
                </label>
                <input 
                  type="number" 
                  step="0.01" 
                  required
                  value={actualCash}
                  onChange={e => handleActualCashChange(e.target.value)}
                  className="input input-bordered w-full font-bold text-base text-gray-900 border-gray-300 focus:border-rose-500"
                  placeholder="0.00"
                />
                <span className="text-[11px] text-gray-400">Total physical currency notes & coins counted.</span>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Actual QR / Online Received
                </label>
                <input 
                  type="number" 
                  step="0.01" 
                  value={actualQr}
                  onChange={e => setActualQr(e.target.value)}
                  className="input input-bordered w-full font-bold text-base text-gray-900 border-gray-300"
                  placeholder="0.00"
                />
                <span className="text-[11px] text-gray-400">Verified via merchant app / settlement log.</span>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Actual Shift Expenses Paid
                </label>
                <input 
                  type="number" 
                  step="0.01" 
                  value={actualExpenses}
                  onChange={e => setActualExpenses(e.target.value)}
                  className="input input-bordered w-full font-bold text-base text-gray-900 border-gray-300"
                  placeholder="0.00"
                />
                <span className="text-[11px] text-gray-400">Petty cash spent during this shift.</span>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Closing Float Left For Next Shift
                </label>
                <input 
                  type="number" 
                  step="0.01" 
                  value={closingFloatRetained}
                  onChange={e => handleRetainedChange(e.target.value)}
                  className="input input-bordered w-full font-bold text-base text-gray-900 border-gray-300"
                  placeholder="0.00"
                />
                <span className="text-[11px] text-gray-400">Cash left in drawer for the incoming cashier.</span>
              </div>
            </div>

            {/* Handover & Deposit Calculation */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-2xl bg-gray-50 border">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Cash Handed Over / Safe Deposit
                </label>
                <input 
                  type="number" 
                  step="0.01" 
                  value={cashDeposited}
                  onChange={e => setCashDeposited(e.target.value)}
                  className="input input-bordered w-full font-bold text-base text-emerald-800"
                  placeholder="0.00"
                />
                <span className="text-[11px] text-gray-400">Cash in hand ({parsedActualCash.toFixed(2)}) - Float retained ({parsedRetained.toFixed(2)})</span>
              </div>

              {/* LIVE VARIANCE BADGE */}
              <div className="flex flex-col justify-center">
                <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                  Cash Reconciliation Variance
                </div>
                <div className={`p-2.5 rounded-xl border flex items-center justify-between font-bold text-sm ${
                  Math.abs(cashDifference) < 0.01 
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800' 
                    : cashDifference > 0 
                      ? 'bg-blue-50 border-blue-200 text-blue-800' 
                      : 'bg-rose-50 border-rose-200 text-rose-800'
                }`}>
                  <div className="flex items-center gap-1.5">
                    {Math.abs(cashDifference) < 0.01 ? (
                      <IconCheck size={18} className="text-emerald-600" />
                    ) : (
                      <IconAlertCircle size={18} className={cashDifference > 0 ? "text-blue-600" : "text-rose-600"} />
                    )}
                    <span>
                      {Math.abs(cashDifference) < 0.01 
                        ? 'Balanced (Exact)' 
                        : cashDifference > 0 
                          ? 'Cash Surplus / Over' 
                          : 'Cash Deficit / Short'}
                    </span>
                  </div>
                  <span>{cashDifference >= 0 ? `+${currency}` : `-${currency}`}{Math.abs(cashDifference).toFixed(2)}</span>
                </div>
                <span className="text-[11px] text-gray-400 mt-1">Expected: {currency}{currentExpectedCash.toFixed(2)} | Counted: {currency}{parsedActualCash.toFixed(2)}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                Handover & Discrepancy Remarks
              </label>
              <textarea
                rows={2}
                placeholder="Any difference explanation, voucher notes, or drawer handover instructions"
                value={closingNote}
                onChange={e => setClosingNote(e.target.value)}
                className="textarea textarea-bordered w-full text-xs"
              ></textarea>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t">
              <button 
                type="button" 
                onClick={() => setIsEndShiftModalOpen(false)} 
                className="btn btn-sm btn-ghost"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={endingShiftLoading}
                className="btn btn-sm bg-rose-600 hover:bg-rose-700 text-white border-none gap-1 px-4"
              >
                {endingShiftLoading ? <span className="loading loading-spinner loading-xs"></span> : <IconCheck size={16} />}
                Confirm & Close Register
              </button>
            </div>
          </form>
        </div>
      </dialog>

      {/* SHIFT CLOSING VOUCHER (Z-REPORT) MODAL & PRINTABLE SLIP */}
      <dialog className={`modal ${isVoucherModalOpen ? 'modal-open' : ''}`} open={isVoucherModalOpen}>
        <div className="modal-box max-w-2xl border border-gray-300 rounded-3xl p-0 overflow-hidden shadow-2xl bg-white">
          <div className="no-print bg-gray-900 p-4 text-white flex justify-between items-center">
            <h3 className="font-bold text-base flex items-center gap-2">
              <IconFileText size={18} /> Register Shift Closing Voucher (Z-Report)
            </h3>
            <div className="flex items-center gap-2">
              <button 
                onClick={handlePrintVoucher} 
                className="btn btn-sm btn-primary gap-1"
              >
                <SafeIconPrinter size={16} /> Print Voucher
              </button>
              <button 
                onClick={() => setIsVoucherModalOpen(false)} 
                className="btn btn-sm btn-circle btn-ghost text-white"
              >
                ✕
              </button>
            </div>
          </div>

          {selectedVoucherShift && (
            <div className={`p-6 md:p-8 voucher-printable ${printMode === 'voucher' ? 'print-active' : ''}`}>
              {/* Voucher Header */}
              <div className="text-center border-b pb-4 mb-4">
                <h2 className="text-xl font-black uppercase text-black">{storeName}</h2>
                {storeAddress && <p className="text-xs text-gray-700">{storeAddress}</p>}
                <div className="flex justify-center items-center gap-3 text-xs text-gray-700">
                  {storePhone && <span>Tel: {storePhone}</span>}
                  {storePan && <span>PAN/VAT: {storePan}</span>}
                </div>
                <h3 className="text-sm font-bold uppercase tracking-widest text-black mt-2 underline">
                  REGISTER SHIFT CLOSING REPORT (Z-REPORT)
                </h3>
              </div>

              {/* Shift Metadata */}
              <div className="grid grid-cols-2 gap-2 text-xs border-b pb-3 mb-3 text-gray-800">
                <div>
                  <p><span className="font-bold">Shift No:</span> #{selectedVoucherShift.shift_number}</p>
                  <p><span className="font-bold">Opened By:</span> {selectedVoucherShift.opened_by_name || 'Cashier'}</p>
                  <p><span className="font-bold">Opened At:</span> {formatDateTime(selectedVoucherShift.opened_at)}</p>
                </div>
                <div className="text-right">
                  <p><span className="font-bold">Status:</span> CLOSED</p>
                  <p><span className="font-bold">Closed By:</span> {selectedVoucherShift.closed_by_name || currentUser?.name || 'Cashier'}</p>
                  <p><span className="font-bold">Closed At:</span> {formatDateTime(selectedVoucherShift.closed_at)}</p>
                </div>
              </div>

              {/* Financial Breakdown Table */}
              <div className="mb-4">
                <h4 className="text-xs font-bold uppercase text-gray-700 mb-1 border-b pb-1">1. Shift Financial Summary</h4>
                <table className="w-full text-xs border-collapse">
                  <tbody>
                    <tr className="border-b">
                      <td className="py-1">Opening Cash Float</td>
                      <td className="py-1 text-right font-bold">{currency}{parseFloat(selectedVoucherShift.opening_balance ?? selectedVoucherShift.starting_float ?? 0).toFixed(2)}</td>
                    </tr>
                    {selectedVoucherShift.payment_breakdown && selectedVoucherShift.payment_breakdown.length > 0 ? (
                      selectedVoucherShift.payment_breakdown.map((pb, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="py-1">Sales ({pb.payment_method})</td>
                          <td className="py-1 text-right font-bold">{currency}{parseFloat(pb.total_amount || 0).toFixed(2)}</td>
                        </tr>
                      ))
                    ) : (
                      <>
                        <tr className="border-b">
                          <td className="py-1">Total Cash Sales</td>
                          <td className="py-1 text-right font-bold">{currency}{parseFloat(selectedVoucherShift.system_cash_sales || 0).toFixed(2)}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-1">Total QR / UPI Sales</td>
                          <td className="py-1 text-right font-bold">{currency}{parseFloat(selectedVoucherShift.system_qr_sales || 0).toFixed(2)}</td>
                        </tr>
                        {parseFloat(selectedVoucherShift.system_card_sales || 0) > 0 && (
                          <tr className="border-b">
                            <td className="py-1">Total Card Sales</td>
                            <td className="py-1 text-right font-bold">{currency}{parseFloat(selectedVoucherShift.system_card_sales).toFixed(2)}</td>
                          </tr>
                        )}
                      </>
                    )}
                    {parseFloat(selectedVoucherShift.system_due_collected || 0) > 0 && (
                      <tr className="border-b">
                        <td className="py-1">Credit / Due Collected</td>
                        <td className="py-1 text-right font-bold">{currency}{parseFloat(selectedVoucherShift.system_due_collected).toFixed(2)}</td>
                      </tr>
                    )}
                    <tr className="border-b">
                      <td className="py-1 text-rose-700">Shift Expenses Paid</td>
                      <td className="py-1 text-right font-bold text-rose-700">({currency}{parseFloat(selectedVoucherShift.actual_expenses || selectedVoucherShift.system_expenses || 0).toFixed(2)})</td>
                    </tr>
                    {parseFloat(selectedVoucherShift.system_due_paid || 0) > 0 && (
                      <tr className="border-b">
                        <td className="py-1 text-rose-700">Supplier Due Paid</td>
                        <td className="py-1 text-right font-bold text-rose-700">({currency}{parseFloat(selectedVoucherShift.system_due_paid).toFixed(2)})</td>
                      </tr>
                    )}
                    {parseFloat(selectedVoucherShift.shift_cash_transfers_out || 0) > 0 && (
                      <tr className="border-b text-rose-700">
                        <td className="py-1 text-rose-700">Cash Transferred Out (Contra)</td>
                        <td className="py-1 text-right font-bold text-rose-700">({currency}{parseFloat(selectedVoucherShift.shift_cash_transfers_out).toFixed(2)})</td>
                      </tr>
                    )}
                    {parseFloat(selectedVoucherShift.shift_cash_transfers_in || 0) > 0 && (
                      <tr className="border-b text-emerald-700">
                        <td className="py-1 text-emerald-700">Cash Transferred In (Contra)</td>
                        <td className="py-1 text-right font-bold text-emerald-700">+{currency}{parseFloat(selectedVoucherShift.shift_cash_transfers_in).toFixed(2)}</td>
                      </tr>
                    )}
                    {parseFloat(selectedVoucherShift.shift_cash_journal_out || 0) > 0 && (
                      <tr className="border-b text-rose-700">
                        <td className="py-1 text-rose-700">Cash Journal Outflow</td>
                        <td className="py-1 text-right font-bold text-rose-700">({currency}{parseFloat(selectedVoucherShift.shift_cash_journal_out).toFixed(2)})</td>
                      </tr>
                    )}
                    {parseFloat(selectedVoucherShift.shift_cash_journal_in || 0) > 0 && (
                      <tr className="border-b text-emerald-700">
                        <td className="py-1 text-emerald-700">Cash Journal Inflow</td>
                        <td className="py-1 text-right font-bold text-emerald-700">+{currency}{parseFloat(selectedVoucherShift.shift_cash_journal_in).toFixed(2)}</td>
                      </tr>
                    )}
                    <tr className="bg-gray-100 font-bold border-t-2 border-black">
                      <td className="py-1.5 font-bold">System Expected Cash in Drawer</td>
                      <td className="py-1.5 text-right font-black">{currency}{parseFloat(selectedVoucherShift.system_expected_cash || 0).toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Physical Count & Audit Table */}
              <div className="mb-4">
                <h4 className="text-xs font-bold uppercase text-gray-700 mb-1 border-b pb-1">2. Physical Drawer Audit & Reconciliation</h4>
                <table className="w-full text-xs border-collapse">
                  <tbody>
                    <tr className="border-b">
                      <td className="py-1">Actual Physical Cash Counted</td>
                      <td className="py-1 text-right font-bold text-base">{currency}{parseFloat(selectedVoucherShift.actual_cash_in_hand || 0).toFixed(2)}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1">Actual QR / Online Verified</td>
                      <td className="py-1 text-right font-bold">{currency}{parseFloat(selectedVoucherShift.actual_qr_in_hand || 0).toFixed(2)}</td>
                    </tr>
                    <tr className="border-b font-bold">
                      <td className="py-1">Cash Difference / Variance</td>
                      <td className={`py-1 text-right ${
                        parseFloat(selectedVoucherShift.cash_difference ?? selectedVoucherShift.difference ?? 0) === 0 
                          ? 'text-emerald-700' 
                          : parseFloat(selectedVoucherShift.cash_difference ?? selectedVoucherShift.difference ?? 0) > 0 
                            ? 'text-blue-700' 
                            : 'text-rose-700'
                      }`}>
                        {parseFloat(selectedVoucherShift.cash_difference ?? selectedVoucherShift.difference ?? 0) >= 0 ? '+' : ''}
                        {currency}{parseFloat(selectedVoucherShift.cash_difference ?? selectedVoucherShift.difference ?? 0).toFixed(2)}
                        {parseFloat(selectedVoucherShift.cash_difference ?? selectedVoucherShift.difference ?? 0) === 0 ? ' (Balanced)' : parseFloat(selectedVoucherShift.cash_difference ?? selectedVoucherShift.difference ?? 0) > 0 ? ' (Over)' : ' (Short)'}
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1">Closing Float Retained for Next Shift</td>
                      <td className="py-1 text-right font-bold">{currency}{parseFloat(selectedVoucherShift.closing_balance || 0).toFixed(2)}</td>
                    </tr>
                    <tr className="bg-emerald-50 border-t-2 border-emerald-600 font-bold">
                      <td className="py-1.5 text-emerald-900">Net Cash Handed Over / Safe Deposit</td>
                      <td className="py-1.5 text-right font-black text-emerald-900 text-sm">
                        {currency}{parseFloat(selectedVoucherShift.cash_deposited || (selectedVoucherShift.actual_cash_in_hand - selectedVoucherShift.closing_balance) || 0).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* 3. Physical Cash Denomination Breakdown (Start & End Shift) */}
              {(() => {
                let startDenoms = selectedVoucherShift.start_denominations;
                if (typeof startDenoms === 'string') {
                  try { startDenoms = JSON.parse(startDenoms); } catch (e) { startDenoms = null; }
                }
                if (!Array.isArray(startDenoms)) startDenoms = null;

                let endDenoms = selectedVoucherShift.denominations;
                if (typeof endDenoms === 'string') {
                  try { endDenoms = JSON.parse(endDenoms); } catch (e) { endDenoms = null; }
                }
                if (!Array.isArray(endDenoms)) endDenoms = null;

                if (!startDenoms && !endDenoms) return null;

                const renderDenomCards = (denomList) => (
                  <div className="space-y-2">
                    {denomList.map((currGroup, idx) => (
                      <div key={idx} className="border border-gray-300 rounded-lg p-2.5 bg-gray-50/60">
                        <div className="flex justify-between items-center text-xs font-bold text-gray-800 border-b border-gray-200 pb-1 mb-1.5">
                          <span className="flex items-center gap-1">
                            <span className="font-extrabold">{currGroup.currency_code}</span> ({currGroup.currency_symbol})
                            {!currGroup.is_base && (
                              <span className="text-[10px] text-gray-500 font-normal">
                                @ Exch Rate {currGroup.exchange_rate}
                              </span>
                            )}
                          </span>
                          <span className="text-right">
                            Total: {currGroup.currency_symbol}{(currGroup.currency_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            {!currGroup.is_base && (
                              <span className="text-emerald-700 font-bold ml-1.5">
                                (≈ {currency}{(currGroup.base_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5 text-xs text-gray-800">
                          {(currGroup.breakdown || []).map((item, bIdx) => (
                            <div key={bIdx} className="flex justify-between items-center px-2 py-1 bg-white rounded border border-gray-200 shadow-2xs">
                              <span className="font-medium text-[11px] text-gray-600">
                                {currGroup.currency_symbol}{item.denomination} × <strong className="text-black">{item.count}</strong>
                              </span>
                              <span className="font-bold text-[11px] text-gray-900">
                                {currGroup.currency_symbol}{item.total.toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );

                return (
                  <div className="mb-4 space-y-4">
                    {startDenoms && startDenoms.length > 0 && (
                      <div>
                        <h4 className="text-xs font-bold uppercase text-gray-700 mb-1 border-b pb-1">
                          3A. Start Shift / Opening Cash Denomination Breakdown
                        </h4>
                        {renderDenomCards(startDenoms)}
                      </div>
                    )}
                    {endDenoms && endDenoms.length > 0 && (
                      <div>
                        <h4 className="text-xs font-bold uppercase text-gray-700 mb-1 border-b pb-1">
                          {startDenoms && startDenoms.length > 0 ? "3B. End Shift / Closing Counted Cash Denomination Breakdown" : "3. Counted Cash Denomination Breakdown"}
                        </h4>
                        {renderDenomCards(endDenoms)}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Notes */}
              {(selectedVoucherShift.notes || selectedVoucherShift.opening_note || selectedVoucherShift.closing_note) && (
                <div className="mb-4 text-xs bg-gray-50 p-2.5 rounded-lg border">
                  {selectedVoucherShift.notes && <p><span className="font-bold">Notes:</span> {selectedVoucherShift.notes}</p>}
                  {selectedVoucherShift.opening_note && <p><span className="font-bold">Opening Note:</span> {selectedVoucherShift.opening_note}</p>}
                  {selectedVoucherShift.closing_note && <p><span className="font-bold">Closing Note:</span> {selectedVoucherShift.closing_note}</p>}
                </div>
              )}

              {/* 3-COLUMN SIGNATURE BLOCK */}
              <div className="signature-section mt-8 pt-6 border-t border-gray-300">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="flex flex-col items-center">
                    <div className="w-32 border-b border-dashed border-gray-500 h-10 mb-1"></div>
                    <span className="font-bold text-[11px] uppercase text-black">Prepared By</span>
                    <span className="text-[10px] text-gray-600">({selectedVoucherShift.closed_by_name || 'Cashier'})</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-32 border-b border-dashed border-gray-500 h-10 mb-1"></div>
                    <span className="font-bold text-[11px] uppercase text-black">Verified By</span>
                    <span className="text-[10px] text-gray-600">(Supervisor)</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-32 border-b border-dashed border-gray-500 h-10 mb-1"></div>
                    <span className="font-bold text-[11px] uppercase text-black">Approved By</span>
                    <span className="text-[10px] text-gray-600">(Manager / Owner)</span>
                  </div>
                </div>
              </div>

            </div>
          )}
        </div>
      </dialog>

      {/* SHIFTS HISTORY MODAL */}
      <dialog className={`modal ${isHistoryModalOpen ? 'modal-open' : ''}`} open={isHistoryModalOpen}>
        <div className="modal-box max-w-4xl border border-gray-200 rounded-3xl p-0 overflow-hidden shadow-2xl bg-white">
          <div className="bg-gray-900 p-6 text-white flex justify-between items-center">
            <div>
              <h3 className="font-black text-xl flex items-center gap-2">
                <IconHistory size={24} /> Closed Shifts & Register Handover History
              </h3>
              <p className="text-xs text-gray-400 mt-1">Audit trail of past registers, declared amounts, variances, and printable Z-reports</p>
            </div>
            <button 
              onClick={() => setIsHistoryModalOpen(false)} 
              className="btn btn-sm btn-circle btn-ghost text-white"
            >
              ✕
            </button>
          </div>

          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {loadingHistory ? (
              <div className="text-center py-10">
                <span className="loading loading-spinner loading-md"></span>
                <p className="text-xs text-gray-500 mt-2">Loading shifts history...</p>
              </div>
            ) : shiftsHistory.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <IconHistory size={36} className="mx-auto mb-2 opacity-50" />
                <p className="font-bold">No closed shifts recorded yet</p>
                <p className="text-xs">Once a register shift is closed, its Z-Report and reconciliation will appear here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table table-sm w-full">
                  <thead>
                    <tr className="bg-gray-100 text-gray-700">
                      <th>Shift #</th>
                      <th>Cashier</th>
                      <th>Opened</th>
                      <th>Closed</th>
                      <th className="text-right">Opening Float</th>
                      <th className="text-right">Expected Cash</th>
                      <th className="text-right">Actual Count</th>
                      <th className="text-right">Variance</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shiftsHistory.map((s) => {
                      const diff = parseFloat(s.cash_difference ?? s.difference ?? 0);
                      return (
                        <tr key={s.id} className="hover:bg-gray-50 border-b">
                          <td className="font-bold">#{s.shift_number}</td>
                          <td>
                            <div className="font-semibold text-xs">{s.closed_by_name || s.opened_by_name || 'Cashier'}</div>
                            {s.status === 'open' && <span className="badge badge-success badge-xs">Open</span>}
                          </td>
                          <td className="text-xs text-gray-500">{formatDateTime(s.opened_at)}</td>
                          <td className="text-xs text-gray-500">{formatDateTime(s.closed_at)}</td>
                          <td className="text-right font-medium text-xs">{currency}{parseFloat(s.opening_balance ?? s.starting_float ?? 0).toFixed(2)}</td>
                          <td className="text-right font-medium text-xs">{currency}{parseFloat(s.system_expected_cash || 0).toFixed(2)}</td>
                          <td className="text-right font-bold text-xs">{currency}{parseFloat(s.actual_cash_in_hand || 0).toFixed(2)}</td>
                          <td className={`text-right font-bold text-xs ${diff === 0 ? 'text-emerald-600' : diff > 0 ? 'text-blue-600' : 'text-rose-600'}`}>
                            {diff >= 0 ? '+' : ''}{currency}{diff.toFixed(2)}
                          </td>
                          <td className="text-center">
                            <button
                              onClick={() => {
                                setIsHistoryModalOpen(false);
                                handleViewVoucher(s);
                              }}
                              className="btn btn-xs btn-outline btn-primary gap-1"
                            >
                              <IconFileText size={12} /> Z-Report
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </dialog>

      {/* PRINT CSS STYLES FOR CLEAN WHITE PAPER */}
      <style>{`
        @media print {
          dialog.modal,
          .modal,
          .modal-box {
            display: block !important;
            position: static !important;
            overflow: visible !important;
            max-height: none !important;
            max-width: 100% !important;
            width: 100% !important;
            border: none !important;
            box-shadow: none !important;
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          body * {
            visibility: hidden;
          }
          .no-print,
          .no-print * {
            display: none !important;
          }
          .print-active, .print-active * {
            visibility: visible !important;
          }
          .daybook-printable.print-active {
            display: block !important;
            visibility: visible !important;
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            background: white !important;
            color: black !important;
            padding: 20px !important;
            margin: 0 !important;
          }
          .voucher-printable.print-active {
            display: block !important;
            visibility: visible !important;
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            max-width: 700px !important;
            margin: 0 auto !important;
            background: white !important;
            color: black !important;
            padding: 20px !important;
          }
          .report-card {
            border: none !important;
            box-shadow: none !important;
            padding: 0 !important;
          }
          .signature-section {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}
